import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import API_CONFIG, { getApiUrl } from "../../config/api";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SettingsModal from "../../components/survey/SettingsModal";
import {
  BsTextParagraph,
  BsInputCursor,
  BsListCheck,
  BsEnvelope,
} from "react-icons/bs";

import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  OutlinedInput,
  InputAdornment,
  FormControlLabel,
  Switch,
  Drawer,
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Grid,
  List,
  ListItem,
  Fab,
  Tooltip,
  Zoom,
  Checkbox,
  Radio,
  Link,
  Slider,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Visibility as PreviewIcon,
  Settings as SettingsIcon,
  Publish as PublishIcon,
  Delete as DeleteIcon,
  TextFields as TextIcon,
  RadioButtonChecked as RadioIcon,
  CheckBox as CheckboxIcon,
  Subject as LongTextIcon,
  Email as EmailIcon,
  Numbers as NumberIcon,
  CalendarMonth as DateIcon,
  Upload as FileIcon,
  Star as RatingIcon,
  Phone as PhoneIcon,
  Numbers as NumbersIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  MenuOpen as MenuOpenIcon,
  Close as CloseIcon,
  AccountTree as AccountTreeIcon,
  ViewList as ListViewIcon,
  GridView as GridViewIcon,
  Search as SearchIcon,
  TableChart as MatrixIcon,
  AccessTime as TimeIcon,
  LinearScale as ScaleIcon,
  Sort as RankingIcon,
  EventNote as DateTimeIcon,
  Language as WebsiteIcon,
  AttachMoney as PriceIcon,
  LocationOn as AddressIcon,
  Looks3 as NpsIcon,
  CompareArrows as LikertIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  DragIndicator,
  Home as HomeIcon,
  LocationCity as LocationCityIcon,
  Terrain as TerrainIcon,
  MarkunreadMailbox as MarkunreadMailboxIcon,
  Public as PublicIcon,
} from "@mui/icons-material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ImageIcon from "@mui/icons-material/Image";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import "./SurveyEditor.css";
import QuestionOptions from "../../components/survey/QuestionOptions";
import SafePreviewSurvey from "../../components/survey/SafePreviewSurvey";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import PublishModal from "../../components/survey/PublishModal";
import LogicRulesModal from "../../components/survey/LogicRulesModal";
import ComingSoonDialog from "../../components/survey/ComingSoonDialog";
import { uploadFile } from "../../services/fileService";
import BugReportButton from "../../components/BugReportButton";
import { setClarityHost, sendClarityEvent, setClarityUser } from "../../utils/clarity";
import { getImageChoiceOptions } from "../../utils/questionOptions";

const getPlaceholderTitle = (type) => {
  const placeholders = {
    "short-answer": "Enter a short answer question",
    "text": "Enter a short answer question",
    "long-answer": "Enter a long answer question",
    "multiple-choice": "Enter your multiple choice question",
    "multiple_choice": "Enter your multiple choice question",
    checkboxes: "Enter your checkbox question",
    email: "Enter email collection question",
    number: "Enter numeric question",
    date: "Enter date question",
    phone: "Enter phone number question",
    "file-upload": "Enter file upload question",
    rating: "Enter rating question",
  };
  return placeholders[type] || "Enter question";
};

const QuestionComponent = ({
  question,
  onChange,
  onDelete,
  onAddBelow,
  index,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  user,
  surveyId,
  setTempImages,
  hasError,
  errorInfo,
  isSelected,
  onSelect,
  allQuestions,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  useEffect(() => {
    if (isNew) {
      const timer = setTimeout(() => setIsNew(false), 2500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Process image-choice options to ensure proper structure when component mounts
  useEffect(() => {
    if (question.type === 'image-choice' && question.options) {
      let needsUpdate = false;
      let processedOptions = [];

      // Handle different data formats from database
      if (typeof question.options === 'string') {
        try {
          const parsed = JSON.parse(question.options);
          if (Array.isArray(parsed)) {
            processedOptions = parsed;
            needsUpdate = true;
          } else if (parsed.options && Array.isArray(parsed.options)) {
            processedOptions = parsed.options;
            needsUpdate = true;
          }
        } catch (e) {
          console.warn('Failed to parse image-choice options from string:', e);
          processedOptions = [];
          needsUpdate = true;
        }
      } else if (Array.isArray(question.options)) {
        processedOptions = question.options.map((opt, idx) => {
          if (typeof opt === 'string') {
            try {
              const parsed = JSON.parse(opt);
              return {
                label: parsed.label || opt,
                imageUrl: parsed.imageUrl || null,
                tempId: parsed.tempId || null,
                showDescription: parsed.showDescription || false,
                description: parsed.description || '',
              };
            } catch (e) {
              return {
                label: opt,
                imageUrl: null,
                tempId: null,
                showDescription: false,
                description: '',
              };
            }
          } else if (typeof opt === 'object' && opt !== null) {
            return {
              label: opt.label || `Option ${idx + 1}`,
              imageUrl: opt.imageUrl || null,
              tempId: opt.tempId || null,
              showDescription: opt.showDescription || false,
              description: opt.description || '',
            };
          } else {
            return {
              label: `Option ${idx + 1}`,
              imageUrl: null,
              tempId: null,
              showDescription: false,
              description: '',
            };
          }
        });
      } else if (typeof question.options === 'object' && question.options !== null) {
        // Handle case where options is an object with nested options array
        if (question.options.options && Array.isArray(question.options.options)) {
          processedOptions = question.options.options;
          needsUpdate = true;
        }
      }

      // Update the question with processed options if needed
      if (needsUpdate && processedOptions.length > 0) {
        const currentOptionsStr = JSON.stringify(question.options);
        const processedOptionsStr = JSON.stringify(processedOptions);
        if (currentOptionsStr !== processedOptionsStr) {
          console.log('Updating image-choice options for question:', question.id, processedOptions);
          onChange('options', processedOptions);
        }
      }
    }
  }, [question.type, question.options, question.id]);

  const formatText = (text, format) => {
    if (!text) return "";
    switch (format) {
      case "sentence":
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
      case "title":
        return text
          .split(" ")
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(" ");
      case "lower":
        return text.toLowerCase();
      case "upper":
        return text.toUpperCase();
      default:
        return text;
    }
  };
  const decodeHTMLEntities = (text) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  };

  const encodeHTMLEntities = (text) => {
    const textArea = document.createElement("textarea");
    textArea.textContent = text;
    return textArea.innerHTML;
  };

  const handleTitleChange = (e) => {
    const rawValue = e.target.value;
    const encodedValue = encodeHTMLEntities(rawValue);
    onChange("title", encodedValue);
  };

  const handleRequiredChange = (e) => {
    onChange("required", e.target.checked);
  };

  // Helper function to get values from options object with fallbacks
  const getOptionValue = (field, defaultValue = "") => {
    return question.options?.[field] !== undefined
      ? question.options[field]
      : question[field] !== undefined
        ? question[field]
        : defaultValue;
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const markedOptions = (question.choices || [])
        .map((opt, idx) => ({
          value: opt || `empty_${idx}`,
          toDelete: true,
        }))
        .filter((m) => m.value !== "Other");
      onChange("markedForDeletion", markedOptions);
    } else {
      onChange("markedForDeletion", []);
    }
  };

  const handleOptionCheck = (option, checked, idx) => {
    const currentMarked = question.markedForDeletion || [];
    const optionIdentifier = option || `empty_${idx}`; // Use index for empty options

    if (checked) {
      // Add the option to marked list if not already present
      if (!currentMarked.find((m) => m.value === optionIdentifier)) {
        onChange("markedForDeletion", [
          ...currentMarked,
          { value: optionIdentifier, toDelete: true },
        ]);
      }
    } else {
      // Remove only the unchecked option
      onChange(
        "markedForDeletion",
        currentMarked.filter((m) => m.value !== optionIdentifier)
      );
    }
  };

  const handleDeleteSelected = () => {
    const markedForDeletion = question.markedForDeletion || [];
    const newOptions = (question.choices || []).filter(
      (opt, idx) =>
        !markedForDeletion.find((m) => m.value === (opt || `empty_${idx}`))
    );
    onChange("choices", newOptions);
    onChange("markedForDeletion", []);
  };

  const handleOptionChange = (idx, value) => {
    const newOptions = [...(question.choices || [])];
    newOptions[idx] = formatText(value, question.textFormat);
    onChange("choices", newOptions);

    if (question.markedForDeletion) {
      const newMarked = question.markedForDeletion.filter(
        (m) => m.value !== newOptions[idx]
      );
      onChange("markedForDeletion", newMarked);
    }
  };

  const handleOptionDelete = (idx) => {
    if (question.type === "image-choice") {
      const currentOptions = Array.isArray(question.options) ? question.options : [];
      const newOptions = currentOptions.filter((_, index) => index !== idx);
      onChange("options", newOptions);
    } else {
      const newOptions = [...(question.choices || [])].filter(
        (_, index) => index !== idx
      );
      onChange("choices", newOptions);
    }
  };

  const renderQuestionPreview = () => {
    switch (question.type) {
      case "text":
      case "short-answer":
        return <input type="text" placeholder="Short answer text" disabled />;
      case "long-answer":
        return <textarea placeholder="Long answer text" disabled rows={4} />;
      case "email":
        return <input type="email" placeholder="email@example.com" disabled />;

      case "dropdown":
        return (
          <div className="options">
            {(question.choices || []).length === 0 &&
              // Initialize with two empty options if empty
              setTimeout(() => {
                onChange("choices", ["", ""]);
              }, 0)}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      question.markedForDeletion?.length ===
                      question.choices?.length
                    }
                    indeterminate={
                      question.markedForDeletion?.length > 0 &&
                      question.markedForDeletion?.length <
                      question.choices?.length
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                }
                label="Select All"
              />
              {question.markedForDeletion?.length > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteSelected}
                  startIcon={<DeleteIcon />}
                  size="small"
                >
                  Delete Selected ({question.markedForDeletion.length})
                </Button>
              )}
            </Box>

            {(question.choices || []).map((option, idx) => (
              <div key={idx} className="option">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    checked={Boolean(
                      question.markedForDeletion?.find(
                        (m) => m.value === (option || `empty_${idx}`)
                      )
                    )}
                    onChange={(e) =>
                      handleOptionCheck(option, e.target.checked, idx)
                    }
                  />
                  <ArrowDropDownIcon sx={{ color: "text.secondary" }} />
                </Box>
                <div className="option-input-wrapper">
                  <input
                    type="text"
                    placeholder={`Option ${idx + 1}`}
                    value={option || ""}
                    onChange={(e) => handleOptionChange(idx, e.target.value)}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleOptionDelete(idx)}
                    className="delete-option-button"
                    sx={{
                      position: "absolute",
                      right: -8,
                      top: -8,
                      opacity: 0,
                      backgroundColor: "white",
                      border: "1px solid #e6e8eb",
                      padding: "4px",
                      "& .MuiSvgIcon-root": {
                        fontSize: "16px",
                      },
                      "&:hover": {
                        backgroundColor: "#f8f9fa",
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            ))}

            <button
              className="add-option"
              onClick={() => {
                const newOptions = [...(question.choices || []), ""];
                onChange("choices", newOptions);
              }}
            >
              Add Option
            </button>
          </div>
        );
      case "number":
        return (
          <OutlinedInput
            type="number"
            placeholder="0"
            disabled
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <NumbersIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            }
            sx={{
              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
              {
                "-webkit-appearance": "none",
                margin: 0,
              },
              "& input[type=number]": {
                "-moz-appearance": "textfield",
              },
              bgcolor: "background.paper",
              "&.Mui-disabled": {
                bgcolor: "grey.50",
                "& .MuiInputAdornment-root": {
                  color: "text.disabled",
                },
              },
            }}
          />
        );
      case "date":
        return <input type="date" disabled />;
      case "phone":
        return (
          <OutlinedInput
            type="tel"
            placeholder="+1 (123) 456-7890"
            disabled
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <PhoneIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            }
            sx={{
              bgcolor: "background.paper",
              "& input": {
                fontFamily: "monospace",
                letterSpacing: "0.5px",
                fontSize: "1rem",
              },
              "&.Mui-disabled": {
                bgcolor: "grey.50",
                "& .MuiInputAdornment-root": {
                  color: "text.disabled",
                },
              },
            }}
          />
        );
      case "file-upload":
        return (
          <Box>
            <input
              type="file"
              id={`file-preview-${question.id}`}
              style={{ display: 'none' }}
              accept={question.allowedFileTypes
                ?.map((type) => `.${type}`)
                .join(",")}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (file) {
                  try {
                    const fileUrl = await uploadFile(
                      file,
                      user.uid,
                      surveyId,
                      question.id
                    );

                    onChange("fileUrl", fileUrl);
                  } catch (error) {
                    console.error("File upload error:", error);
                  }
                }
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <label htmlFor={`file-preview-${question.id}`}>
                <Button
                  variant="outlined"
                  component="span"
                  disabled
                  sx={{
                    bgcolor: 'grey.50',
                    color: 'text.disabled',
                    borderColor: 'grey.300'
                  }}
                >
                  Choose File
                </Button>
              </label>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Maximum file size: 5MB
              </Typography>
            </Box>
            {question.fileUrl && (
              <Box sx={{ mt: 1 }}>
                <Link href={question.fileUrl} target="_blank">
                  View Uploaded File
                </Link>
              </Box>
            )}
          </Box>
        );
      case "multiple-choice":
      case "multiple_choice":
      case "checkboxes":
        return (
          <div className="options">
            {(question.choices || []).length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        question.markedForDeletion?.length ===
                        question.choices.filter((opt) => opt !== "Other").length
                      }
                      indeterminate={
                        question.markedForDeletion?.length > 0 &&
                        question.markedForDeletion?.length <
                        question.choices.filter((opt) => opt !== "Other")
                          .length
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  }
                  label="Select All"
                />
                {question.markedForDeletion?.length > 0 && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteSelected}
                    startIcon={<DeleteIcon />}
                    size="small"
                  >
                    Delete Selected ({question.markedForDeletion.length})
                  </Button>
                )}
              </Box>
            )}
            {(question.choices || []).map((option, idx) => (
              <div key={idx} className="option">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    checked={Boolean(
                      question.markedForDeletion?.find(
                        (m) => m.value === (option || `empty_${idx}`)
                      )
                    )}
                    onChange={(e) =>
                      handleOptionCheck(option, e.target.checked, idx)
                    }
                    disabled={option === "Other"}
                  />
                  <input
                    type={
                      question.type === "multiple-choice" || "multiple_choice" ? "radio" : "checkbox"
                    }
                    disabled
                  />
                </Box>
                <div className="option-input-wrapper">
                  <input
                    type="text"
                    placeholder={`Option ${idx + 1}`}
                    value={option}
                    onChange={(e) => handleOptionChange(idx, e.target.value)}
                  />
                  {option !== "Other" && (
                    <IconButton
                      size="small"
                      onClick={() => handleOptionDelete(idx)}
                      className="delete-option-button"
                      sx={{
                        position: "absolute",
                        right: -8,
                        top: -8,
                        opacity: 0,
                        backgroundColor: "white",
                        border: "1px solid #e6e8eb",
                        padding: "4px",
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                        },
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </div>
              </div>
            ))}
            <button
              className="add-option"
              onClick={() => {
                const newOptions = [...(question.choices || []), ""];
                onChange("choices", newOptions);
              }}
            >
              Add Option
            </button>
          </div>
        );
      case "rating":
        return (
          <div className="rating-preview">
            {[1, 2, 3, 4, 5].map((num) => (
              <span key={num} className="star">
                ★
              </span>
            ))}
          </div>
        );
      case "matrix":
        // Ensure we have non-empty arrays for rows and columns
        const matrixRows = question.rows?.length
          ? question.rows
          : ["Row 1", "Row 2", "Row 3"];
        const matrixColumns = question.columns?.length
          ? question.columns
          : ["Column 1", "Column 2", "Column 3"];

        return (
          <div className="matrix-preview">
            <div className="matrix-table-container">
              <table className="matrix-table">
                <thead>
                  <tr>
                    <th className="matrix-sticky-cell matrix-cell">
                      {/* Empty header cell */}
                    </th>
                    {matrixColumns.map((col, colIdx) => (
                      <th key={colIdx} className="matrix-cell">
                        <div className="matrix-input-wrapper">
                          <input
                            type="text"
                            value={col}
                            onChange={(e) => {
                              const currentColumns = question.columns?.length ? question.columns : ["Column 1", "Column 2", "Column 3"];
                              const newColumns = [...currentColumns];
                              newColumns[colIdx] = e.target.value;
                              onChange("columns", newColumns);
                            }}
                            placeholder={`Column ${colIdx + 1}`}
                          />
                          <IconButton
                            className="delete-option-button"
                            size="small"
                            onClick={() => {
                              const currentColumns = question.columns?.length ? question.columns : ["Column 1", "Column 2", "Column 3"];
                              const newColumns = currentColumns.filter(
                                (_, i) => i !== colIdx
                              );
                              onChange("columns", newColumns);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixRows.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      <td className="matrix-sticky-cell matrix-cell">
                        <div className="matrix-input-wrapper">
                          <input
                            type="text"
                            value={row}
                            onChange={(e) => {
                              const currentRows = question.rows?.length ? question.rows : ["Row 1", "Row 2", "Row 3"];
                              const newRows = [...currentRows];
                              newRows[rowIdx] = e.target.value;
                              onChange("rows", newRows);
                            }}
                            placeholder={`Row ${rowIdx + 1}`}
                          />
                          <IconButton
                            className="delete-option-button"
                            size="small"
                            onClick={() => {
                              const currentRows = question.rows?.length ? question.rows : ["Row 1", "Row 2", "Row 3"];
                              const newRows = currentRows.filter(
                                (_, i) => i !== rowIdx
                              );
                              onChange("rows", newRows);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </td>
                      {matrixColumns.map((_, colIdx) => (
                        <td key={colIdx} className="matrix-cell">
                          {Boolean(question.allowMultipleSelection) ? (
                            <Checkbox
                              disabled
                              size="small"
                              sx={{
                                color: "grey.400",
                                "&.Mui-disabled": {
                                  color: "grey.400",
                                },
                              }}
                            />
                          ) : (
                            <Radio
                              disabled
                              size="small"
                              sx={{
                                color: "grey.400",
                                "&.Mui-disabled": {
                                  color: "grey.400",
                                },
                              }}
                            />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="matrix-actions">
              <button
                className="matrix-action-button"
                onClick={() => {
                  const currentRows = question.rows?.length ? question.rows : ["Row 1", "Row 2", "Row 3"];
                  const newRows = [...currentRows];
                  newRows.push(`Row ${newRows.length + 1}`);
                  onChange("rows", newRows);
                }}
              >
                <AddIcon fontSize="small" />
                Add Row
              </button>
              <button
                className="matrix-action-button"
                onClick={() => {
                  const currentColumns = question.columns?.length ? question.columns : ["Column 1", "Column 2", "Column 3"];
                  const newColumns = [...currentColumns];
                  newColumns.push(`Column ${newColumns.length + 1}`);
                  onChange("columns", newColumns);
                }}
              >
                <AddIcon fontSize="small" />
                Add Column
              </button>
            </div>
          </div>
        );
      case "likert-scale":
        return (
          <div className="likert-preview">
            <div className="likert-scale">
              {questionTypes["likert-scale"].config.scale.map((option, idx) => (
                <div key={idx} className="likert-option">
                  <div className="likert-radio" />
                  <span className="likert-label">{option}</span>
                </div>
              ))}
            </div>
            <div className="likert-scale-labels">
              <span>Strongly Disagree</span>
              <span>Strongly Agree</span>
            </div>
          </div>
        );
      case "nps":
        const npsMin = question.config?.min || 0;
        const npsMax = question.config?.max || 10;
        const npsRange = Array.from(
          { length: npsMax - npsMin + 1 },
          (_, i) => i + npsMin
        );

        const rangeSize = npsMax - npsMin;
        const detractorThreshold = npsMin + Math.floor(rangeSize * 0.6);
        const passiveThreshold = npsMin + Math.floor(rangeSize * 0.8);

        return (
          <div className="nps-preview">
            <div className="nps-scale">
              {npsRange.map((num) => (
                <div key={num} className="nps-number">
                  <input type="radio" disabled />
                  <label
                    style={{
                      borderColor:
                        num <= detractorThreshold
                          ? "#feb2b2"
                          : num <= passiveThreshold
                            ? "#fbd38d"
                            : "#9ae6b4",
                      color:
                        num <= detractorThreshold
                          ? "#e53e3e"
                          : num <= passiveThreshold
                            ? "#dd6b20"
                            : "#38a169",
                      backgroundColor:
                        num <= detractorThreshold
                          ? "#fff5f5"
                          : num <= passiveThreshold
                            ? "#fffaf0"
                            : "#f0fff4",
                    }}
                  >
                    {num}
                  </label>
                </div>
              ))}
            </div>
            <div className="nps-labels">
              <span>
                {question.config?.labels?.start ||
                  questionTypes.nps.config.labels.start}
              </span>
              <span>
                {question.config?.labels?.end ||
                  questionTypes.nps.config.labels.end}
              </span>
            </div>
          </div>
        );
      case "ranking":
        return (
          <div className="ranking-preview ranking-editor-preview">
            {(question.choices || []).map((option, idx) => (
              <div key={idx} className="ranking-item">
                <span className="ranking-number">{idx + 1}</span>
                <DragIndicator sx={{ color: "text.secondary", fontSize: 20 }} />
                <div className="option-input-wrapper">
                  <input
                    type="text"
                    className="ranking-input"
                    placeholder={`Option ${idx + 1}`}
                    value={option}
                    onChange={(e) => handleOptionChange(idx, e.target.value)}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleOptionDelete(idx)}
                    className="delete-option-button"
                    sx={{
                      position: "absolute",
                      right: -8,
                      top: -8,
                      opacity: 0,
                      backgroundColor: "white",
                      border: "1px solid #e6e8eb",
                      "&:hover": {
                        backgroundColor: "#f8f9fa",
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                const newOptions = [...(question.choices || []), ""];
                onChange("choices", newOptions);
              }}
              sx={{
                mt: 2,
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.lighter",
                },
              }}
            >
              Add Option
            </Button>
          </div>
        );

      case "image-choice":
        return (
          <Box sx={{ width: "100%" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={question.allowMultipleSelection || false}
                  onChange={(e) => {
                    onChange("allowMultipleSelection", e.target.checked);
                  }}
                />
              }
              label="Allow more than one answer image to this question (use checkboxes)"
              sx={{ mb: 2 }}
            />
            <Grid
              container
              spacing={2}
              sx={{
                mt: 1,
                "& .image-option": {
                  position: "relative",
                  cursor: "pointer",
                  borderRadius: 1,
                  overflow: "hidden",
                  border: "2px solid",
                  borderColor: "divider",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    transform: "translateY(-4px)",
                  },
                },
              }}
            >
              {(Array.isArray(question.options) ? question.options : []).map((option, idx) => (
                <Grid item xs={12} sm={6} md={12} lg={6} xl={4} key={idx}>
                  <Box className="image-option">
                    <Box sx={{ position: "relative" }}>
                      {(option.imageUrl || option.tempId) ? (
                        <img
                          src={option.imageUrl || (option.tempId && typeof option.tempId === 'object' ? URL.createObjectURL(option.tempId) : option.tempId)}
                          alt={option.label || `Option ${idx + 1}`}
                          style={{
                            width: "100%",
                            height: "auto",
                            aspectRatio: "16/9",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            console.warn('Failed to load image:', option.imageUrl || option.tempId);
                            e.target.style.display = "none";
                            // Show placeholder instead
                            const placeholder = e.target.nextElementSibling;
                            if (placeholder && placeholder.classList.contains('image-placeholder')) {
                              placeholder.style.display = 'flex';
                            }
                          }}
                          onLoad={(e) => {
                            // Hide placeholder when image loads successfully
                            const placeholder = e.target.nextElementSibling;
                            if (placeholder && placeholder.classList.contains('image-placeholder')) {
                              placeholder.style.display = 'none';
                            }
                          }}
                        />
                      ) : null}
                      <Box
                        className="image-placeholder"
                        sx={{
                          bgcolor: "grey.100",
                          aspectRatio: "16/9",
                          display: (option.imageUrl || option.tempId) ? "none" : "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ImageIcon
                          sx={{ fontSize: 40, color: "text.disabled" }}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleOptionDelete(idx)}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          bgcolor: "background.paper",
                          "&:hover": { bgcolor: "background.paper" },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ p: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter label"
                        value={option.label || ""}
                        onChange={(e) => {
                          const currentOptions = Array.isArray(question.options) ? question.options : [];
                          const newOptions = [...currentOptions];
                          newOptions[idx] = {
                            ...newOptions[idx],
                            label: e.target.value,
                          };
                          onChange("options", newOptions);
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={option.showDescription || false}
                            onChange={(e) => {
                              const currentOptions = Array.isArray(question.options) ? question.options : [];
                              const newOptions = [...currentOptions];
                              newOptions[idx] = {
                                ...newOptions[idx],
                                showDescription: e.target.checked,
                              };
                              onChange("options", newOptions);
                            }}
                          />
                        }
                        label="Include descriptive text"
                        sx={{ mt: 1, mb: 1 }}
                      />
                      {option.showDescription && (
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={2}
                          placeholder="Enter image description"
                          value={option.description || ""}
                          onChange={(e) => {
                            const currentOptions = Array.isArray(question.options) ? question.options : [];
                            const newOptions = [...currentOptions];
                            newOptions[idx] = {
                              ...newOptions[idx],
                              description: e.target.value,
                            };
                            onChange("options", newOptions);
                          }}
                          sx={{ mt: 1, mb: 1 }}
                        />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        id={`image-upload-${idx}`}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const tempId = `temp_${Date.now()}_${idx}`;

                            // Store file for later upload
                            setTempImages((prev) => ({
                              ...prev,
                              [tempId]: {
                                file: file,
                                questionId: question.id,
                                optionIndex: idx,
                              },
                            }));
                            // Create temporary URL for preview
                            const imageUrl = URL.createObjectURL(file);

                            const currentOptions = Array.isArray(question.options) ? question.options : [];
                            const newOptions = [...currentOptions];
                            newOptions[idx] = {
                              ...newOptions[idx],
                              imageUrl: imageUrl, // Use URL.createObjectURL for preview
                              tempId: tempId,
                            };
                            onChange("options", newOptions);

                            return () => URL.revokeObjectURL(imageUrl);
                          }
                        }}
                      />
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() =>
                          document.getElementById(`image-upload-${idx}`).click()
                        }
                        sx={{ mt: 1 }}
                      >
                        Upload Image
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                const currentOptions = Array.isArray(question.options) ? question.options : [];
                const newOptions = [
                  ...currentOptions,
                  {
                    imageUrl: "",
                    label: "",
                    showDescription: false,
                    description: "",
                  },
                ];
                onChange("options", newOptions);
              }}
              sx={{ mt: 2 }}
            >
              Add Image Option
            </Button>
          </Box>
        );

      case "range-scale":
        return (
          <Box sx={{ width: "100%", px: { xs: 2, sm: 2 }, mt: 3 }}>
            <Box
              sx={{
                width: "100%",
                px: { xs: 2, sm: 2 }, // Added padding for mark labels
                "& .MuiSlider-root": {
                  // ml: { xs: -2, sm: 0 }, // Compensate for mark label overflow
                  // mr: { xs: -2, sm: 0 },
                  width: { xs: "95%", sm: "100%" }, // Adjust width to prevent overflow
                },
              }}
            >
              <Slider
                value={question.config?.defaultValue || 50}
                min={question.config?.min || 0}
                middle={question.config?.middle}
                max={question.config?.max || 100}
                step={question.config?.step || 1}
                marks={
                  question.config?.showTicks && [
                    {
                      value: question.config?.min || 0,
                      label: question.config?.labels?.start || "Low",
                    },
                    {
                      value:
                        question.config?.middle ||
                        (question.config?.max - question.config?.min) / 2 ||
                        50,
                      label: question.config?.labels?.middle || "Medium",
                    },
                    // { value: (question.config?.max - question.config?.min) / 2 || 50,
                    //   label: question.config?.labels?.middle || 'Medium' },
                    {
                      value: question.config?.max || 100,
                      label: question.config?.labels?.end || "High",
                    },
                  ]
                }
                sx={{
                  "& .MuiSlider-markLabel": {
                    color: "text.secondary",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                gap: 2,
                "& .MuiTextField-root": {
                  // Add this style to make all fields the same width
                  flex: 1,
                  width: { xs: "100%", sm: "25%" }, // Equal width on desktop
                  // minWidth: { sm: '120px' } // Minimum width to prevent too narrow fields
                },
              }}
            >
              <TextField
                label="Min"
                type="number"
                size="small"
                fullWidth={isMobile} // Full width on mobile
                sx={{ flex: { sm: 1 } }} // Equal width on larger screens
                value={question.config?.min || 0}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  onChange("config", {
                    ...question.config,
                    min: value,
                  });
                }}
              />
              <TextField
                label="Middle"
                type="number"
                size="small"
                fullWidth={isMobile}
                sx={{ flex: { sm: 1 } }}
                value={
                  question.config?.middle ||
                  (question.config?.max - question.config?.min) / 2 ||
                  50
                }
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  onChange("config", {
                    ...question.config,
                    middle: value,
                  });
                }}
              />
              <TextField
                label="Max"
                type="number"
                size="small"
                value={question.config?.max || 100}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  onChange("config", {
                    ...question.config,
                    max: value,
                  });
                }}
              />
              <TextField
                label="Step"
                type="number"
                size="small"
                value={question.config?.step || 1}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  onChange("config", {
                    ...question.config,
                    step: value,
                  });
                }}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={question.config?.showTicks || false}
                    onChange={(e) => {
                      onChange("config", {
                        ...question.config,
                        showTicks: e.target.checked,
                      });
                    }}
                  />
                }
                label="Show scale markers"
              />
            </Box>
          </Box>
        );

      case "datetime":
        return (
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {question.config?.labels?.date || "Date"}
              </Typography>
              <OutlinedInput
                type="date"
                fullWidth
                disabled
                placeholder="Select date"
                sx={{
                  bgcolor: "grey.50",
                  "& .MuiInputAdornment-root": {
                    color: "text.disabled",
                  },
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {question.config?.labels?.time || "Time"}
              </Typography>
              <OutlinedInput
                type="time"
                fullWidth
                disabled
                placeholder="Select time"
                sx={{
                  bgcolor: "grey.50",
                  "& .MuiInputAdornment-root": {
                    color: "text.disabled",
                  },
                }}
              />
            </Box>
          </Box>
        );
      case "time":
        return (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {question.config?.labels?.time || "Time"}
            </Typography>
            <OutlinedInput
              type="time"
              fullWidth
              disabled
              placeholder="Select time"
              sx={{
                bgcolor: "grey.50",
                "& .MuiInputAdornment-root": {
                  color: "text.disabled",
                },
              }}
            />
          </Box>
        );
      case "website":
        return (
          <Box sx={{ mt: 1 }}>
            <OutlinedInput
              type="url"
              fullWidth
              disabled
              placeholder="https://example.com"
              startAdornment={
                <InputAdornment position="start">
                  <WebsiteIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              }
              sx={{
                bgcolor: "grey.50",
                "& .MuiInputAdornment-root": {
                  color: "text.disabled",
                },
              }}
            />
          </Box>
        );
      case "address":
        return (
          <Box sx={{ mt: 1 }} className="address-grid">
            <Box className="address-field full-width">
              <OutlinedInput
                fullWidth
                disabled
                placeholder="Street address"
                startAdornment={
                  <InputAdornment position="start">
                    <HomeIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                }
                sx={{
                  bgcolor: "grey.50",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "& .MuiInputBase-input": {
                    pl: 1,
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Box>
            <Box className="address-row">
              <Box className="address-field city">
                <OutlinedInput
                  fullWidth
                  disabled
                  placeholder="City"
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationCityIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  }
                  sx={{
                    bgcolor: "grey.50",
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "divider",
                    },
                    "& .MuiInputBase-input": {
                      pl: 1,
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Box>
              <Box className="address-field state">
                <OutlinedInput
                  fullWidth
                  disabled
                  placeholder="State"
                  startAdornment={
                    <InputAdornment position="start">
                      <TerrainIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  }
                  sx={{
                    bgcolor: "grey.50",
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "divider",
                    },
                    "& .MuiInputBase-input": {
                      pl: 1,
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Box>
              <Box className="address-field zip">
                <OutlinedInput
                  fullWidth
                  disabled
                  placeholder="ZIP code"
                  startAdornment={
                    <InputAdornment position="start">
                      <MarkunreadMailboxIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  }
                  sx={{
                    bgcolor: "grey.50",
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "divider",
                    },
                    "& .MuiInputBase-input": {
                      pl: 1,
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Box>
            </Box>
            <Box className="address-field full-width">
              <OutlinedInput
                fullWidth
                disabled
                placeholder="Country"
                startAdornment={
                  <InputAdornment position="start">
                    <PublicIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                }
                sx={{
                  bgcolor: "grey.50",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "& .MuiInputBase-input": {
                    pl: 1,
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };
  return (
    <div
      className={`question-container ${isNew ? "new-question" : ""} ${hasError ? "has-error" : ""
        }`}
      draggable="true"
      onDragStart={(e) => onDragStart(e, index)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => onDragOver(e, index)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, index)}
    >
      <div className="question-header">
        <div className="question-header-content">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
              flexWrap: isMobile ? "wrap" : "nowrap",
            }}
          >
            {isMobile && (
              <IconButton
                onClick={() => setIsExpanded(!isExpanded)}
                size="small"
                sx={{ p: 0.5 }}
              >
                {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>
            )}
            <input
              type="text"
              placeholder={getPlaceholderTitle(question.type)}
              className="question-title"
              value={decodeHTMLEntities(question.title) || ""}
              onChange={handleTitleChange}
              id={`question-title-${question.id}`}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: 1,
                px: 1,
                py: 0.5,
                "& .MuiSvgIcon-root": {
                  fontSize: "1rem",
                  mr: 0.5,
                  color: "text.secondary",
                },
              }}
            >
              {questionTypes[question.type]?.icon || <HelpOutlineIcon />}
            </Box>

            {/* Logic Indicator */}
            {question.hasLogicRules && (
              <Tooltip title={`${question.logicRulesCount || 0} logic rule(s)`}>
                <IconButton
                  size="small"
                  sx={{
                    color: "primary.main",
                    "& .MuiSvgIcon-root": { fontSize: "1rem" },
                  }}
                  onClick={() => {
                    onChange("selected", true);
                  }}
                >
                  <AccountTreeIcon />
                </IconButton>
              </Tooltip>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                ml: "auto",
              }}
            >
              {!isMobile && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={question.required || false}
                      onChange={handleRequiredChange}
                    />
                  }
                  label="Required"
                  sx={{
                    mr: 1,
                    "& .MuiTypography-root": {
                      fontSize: "0.875rem",
                      color: "text.secondary",
                    },
                  }}
                />
              )}

              {isMobile && (
                <IconButton
                  onClick={() => setOptionsOpen(true)}
                  size="small"
                  sx={{
                    color: "primary.main",
                    "&:hover": { bgcolor: "primary.lighter" },
                  }}
                >
                  <MenuOpenIcon fontSize="small" />
                </IconButton>
              )}

              <IconButton
                onClick={() => setDeleteConfirmOpen(true)}
                size="small"
                sx={{
                  color: "error.main",
                  "&:hover": { bgcolor: "error.lighter" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </div>
      </div>{" "}
      {(!isMobile || isExpanded) && (
        <div className="preview">{renderQuestionPreview()}</div>
      )}{" "}
      {/* Error Message Display */}
      {hasError && errorInfo && (
        <div
          className={`question-error-message ${errorInfo.errors && errorInfo.errors.length > 1
            ? "multiple-errors"
            : ""
            }`}
        >
          <span className="error-icon">⚠</span>
          <div>
            {errorInfo.errors && errorInfo.errors.length > 0 ? (
              errorInfo.errors.length === 1 ? (
                errorInfo.errors[0]
              ) : (
                <ul className="question-error-list">
                  {errorInfo.errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              )
            ) : (
              "This question has validation errors"
            )}
          </div>
        </div>
      )}
      {isMobile && (
        <Drawer
          anchor="right"
          open={optionsOpen}
          onClose={() => setOptionsOpen(false)}
          PaperProps={{
            sx: { width: "100%", maxWidth: "400px" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="h6">Question Options</Typography>
            <IconButton onClick={() => setOptionsOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <QuestionOptions
            question={question}
            onChange={onChange}
            allQuestions={allQuestions}
            surveyId={surveyId || ""}
            userId={user?.uid || ""}
            onClose={() => setOptionsOpen(false)}
          />
        </Drawer>
      )}
      {/* Add Below Button - hidden for now to avoid confusion */}
      {false && isSelected && (
        <Tooltip title="Add question below" placement="top">
          <Fab
            size="small"
            color="secondary"
            onClick={onAddBelow}
            sx={{
              position: "absolute",
              bottom: -20,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
              boxShadow: 2,
              "&:hover": {
                boxShadow: 4,
              },
            }}
          >
            <AddCircleOutlineIcon fontSize="small" />
          </Fab>
        </Tooltip>
      )}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={() => {
          setDeleteConfirmOpen(false);
          onDelete();
        }}
        title="Delete Question"
        message="Are you sure you want to delete this question? This action cannot be undone."
      />
    </div>
  );
};

const questionTypes = {
  "short-answer": {
    label: "Short Answer",
    icon: <TextIcon />,
    placeholder: "Enter your answer",
  },
  //  "text": {
  //   label: "Short Answer",
  //   icon: <TextIcon />,
  //   placeholder: "Enter your answer",
  // },
  "multiple-choice": {
    label: "Multiple Choice",
    icon: <RadioIcon />,
    hasOptions: true,
    placeholder: "Select an option",
  },
  //  "multiple_choice": {
  //   label: "Multiple Choice",
  //   icon: <RadioIcon />,
  //   hasOptions: true,
  //   placeholder: "Select an option",
  // },
  checkboxes: {
    label: "Checkboxes",
    icon: <CheckboxIcon />,
    hasOptions: true,
    placeholder: "Select options",
  },
  dropdown: {
    label: "Dropdown",
    icon: <ArrowDropDownIcon />,
    hasOptions: true,
    placeholder: "Select an option",
    config: {
      searchable: false,
      allowMultiple: false,
    },
  },
  "long-answer": {
    label: "Long Answer",
    icon: <LongTextIcon />,
    placeholder: "Enter your detailed response",
  },
  email: {
    label: "Email",
    icon: <EmailIcon />,
    validation: "email",
    placeholder: "email@example.com",
  },
  number: {
    label: "Number",
    icon: <NumberIcon />,
    validation: "number",
    placeholder: "Enter a number",
  },
  date: {
    label: "Date",
    icon: <DateIcon />,
    validation: "date",
    placeholder: "Select a date",
  },
  "file-upload": {
    label: "File Upload",
    icon: <FileIcon />,
    accepts: ["image/*", "application/pdf", ".doc,.docx"],
    placeholder: "Upload a file",
    validation: {
      maxFileSize: 2,
      allowedFileTypes: [],
      errorMessage: "Invalid file",
    },
  },
  rating: {
    label: "Rating",
    icon: <RatingIcon />,
    config: {
      max: 5,
      showLabels: true,
    },
  },
  phone: {
    label: "Phone Number",
    icon: <PhoneIcon />,
    validation: "phone",
    placeholder: "+1 (123) 456-7890",
  },
  matrix: {
    label: "Matrix",
    icon: <MatrixIcon />,
    hasOptions: true,
    hasRows: true,
    hasColumns: true,
    placeholder: "Matrix question",
  },
  "likert-scale": {
    label: "Likert Scale",
    icon: <LikertIcon />,
    hasOptions: true,
    config: {
      scale: [
        "Strongly Disagree",
        "Disagree",
        "Neutral",
        "Agree",
        "Strongly Agree",
      ],
    },
    placeholder: "Rate your agreement",
  },
  nps: {
    label: "NPS Rating",
    icon: <NpsIcon />,
    config: {
      min: 0,
      max: 10,
      labels: {
        start: "Not likely at all",
        end: "Extremely likely",
      },
    },
    placeholder: "How likely are you to recommend...",
  },
  ranking: {
    label: "Ranking",
    icon: <RankingIcon />,
    hasOptions: true,
    placeholder: "Rank the following options",
  },
  // 'scale': {
  //   label: 'Linear Scale',
  //   icon: <ScaleIcon />,
  //   config: {
  //     min: 1,
  //     max: 5,
  //     step: 1,
  //     labels: {
  //       start: '',
  //       end: ''
  //     }
  //   },
  //   placeholder: 'Rate on a scale'
  // },
  "range-scale": {
    label: "Range Scale",
    icon: <LinearScaleIcon />,
    config: {
      min: 0,
      max: 100,
      step: 1,
      showTicks: true,
      showLabels: true,
      labels: {
        start: "Low",
        middle: "Medium",
        end: "High",
      },
    },
    placeholder: "Select a value on the scale",
  },

  "image-choice": {
    label: "Image Choice",
    icon: <ImageIcon />,
    hasOptions: true,
    hasImageOptions: true,
    placeholder: "Select an image option",
    config: {
      layout: "grid", // or 'list'
      allowMultiple: false,
      imageSize: "medium", // small, medium, large
      showLabels: true,
      showDescriptions: false,
    },
  },

  datetime: {
    label: "Date & Time",
    icon: <DateTimeIcon />,
    config: {
      format: "default",
      showSeconds: false,
      minDate: "",
      maxDate: "",
      labels: {
        date: "Date",
        time: "Time",
      },
    },
    placeholder: "Select date and time",
  },
  time: {
    label: "Time",
    icon: <TimeIcon />,
    config: {
      timeFormat: "24",
      labels: {
        time: "Time",
      },
    },
    placeholder: "Select time",
  },
  website: {
    label: "Website",
    icon: <WebsiteIcon />,
    validation: {
      pattern: "^(https?://)?([w-]+.)+[w-]+(/[w- ./?%&=]*)?$",
    },
    placeholder: "Enter website URL",
    errorMessage: "Please enter a valid website URL",
  },
  // 'price': {
  //   label: 'Price',
  //   icon: <PriceIcon />,
  //   validation: 'price',
  //   config: {
  //     currency: 'USD',
  //     allowDecimals: true
  //   },
  //   placeholder: 'Enter price'
  // },
  address: {
    label: "Address",
    icon: <AddressIcon />,
    hasSubfields: true,
    type: "address",
    subfields: ["street", "city", "state", "zip", "country"],
    placeholder: "Enter address",
    validation: true,
    config: {
      defaultCountry: "US",
    },
  },
};

const SurveyEditor = () => {
  const [tempImages, setTempImages] = useState({});
  const { surveyId } = useParams();
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    try {
      // register survey + user context with Clarity when editor mounts
      setClarityUser(surveyId || null, user?.uid || user?.id || null);
    } catch (e) {
      console.warn('Failed to set clarity user from SurveyEditor', e);
    }
  }, [surveyId, user]);
  const [questions, setQuestions] = useState([]);
  const [initialQuestions, setInitialQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const questionsEndRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [initialSurveyData, setInitialSurveyData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingPositions, setIsUpdatingPositions] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [success, setSuccess] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [surveyLayout, setSurveyLayout] = useState("classic");
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [logicRulesModalOpen, setLogicRulesModalOpen] = useState(false);
  const [comingSoonDialogOpen, setComingSoonDialogOpen] = useState(false);
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [tempTheme, setTempTheme] = useState(null);
  const sidebarScrollPosition = useRef(0);
  const [isQuestionTypesOpen, setIsQuestionTypesOpen] = useState(false);
  const questionTypesRef = useRef(null);
  const [questionErrors, setQuestionErrors] = useState({});

  const handleImageUpload = (image) => {
    setTempImages((prev) => ({ ...prev, [image.id]: image.url }));
  };

  // Modify handleThemeSelect to only update temporary state
  const handleThemeSelect = (theme) => {
    setTempTheme(theme); // Store theme temporarily without saving to DB
  };

  const handleSurveyUpdate = (updatedData) => {
    setSurveyData((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  // Add this effect to handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        questionTypesRef.current &&
        !questionTypesRef.current.contains(event.target)
      ) {
        setIsQuestionTypesOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (surveyData?.theme) {
      // console.log('Theme updated in survey data:', surveyData.theme);
    }
  }, [surveyData?.theme]);

  // Handle error with snackbar and redirect
  useEffect(() => {
    if (error) {
      setSnackbarOpen(true);
      // Auto-redirect to dashboard after 3 seconds

    }
  }, [error]);

  const [dropTarget, setDropTarget] = useState(null);
  const [draggedType, setDraggedType] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [draggedQuestionIndex, setDraggedQuestionIndex] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);

  const sidebarRef = useRef(null);

  const TopBarButtons = () => {
    if (isMobile) {
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={handleSave}
            disabled={isSaving}
            sx={{ color: "primary.main" }}
            size="small"
          >
            <SaveIcon />
          </IconButton>
          <IconButton
            onClick={handlePreviewOpen}
            sx={{ color: "primary.main" }}
            size="small"
          >
            <PreviewIcon />
          </IconButton>
          <IconButton
            onClick={() => setSettingsModalOpen(true)}
            sx={{ color: "primary.main" }}
            size="small"
          >
            <SettingsIcon />
          </IconButton>
          <IconButton
            onClick={() => setComingSoonDialogOpen(true)}
            sx={{ color: "primary.main" }}
            size="small"
          >
            <AccountTreeIcon />
          </IconButton>
          <Box sx={{ mx: 0.5 }}>
            <BugReportButton variant="fab" size="small" />
          </Box>
          <IconButton
            onClick={() => { }}
            sx={{
              color: "white",
              bgcolor: "primary.main",
              "&:hover": {
                bgcolor: "primary.dark",
              },
            }}
            size="small"
          >
            <PublishIcon />
          </IconButton>
        </Box>
      );
    }

    return (
      <>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isSaving}
          sx={{ mr: 1 }}
        >
          Save
        </Button>
        <Button
          variant="outlined"
          startIcon={<PreviewIcon />}
          onClick={handlePreviewOpen}
          sx={{ mr: 1 }}
        >
          Preview
        </Button>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => {
            setSettingsModalOpen(true);
          }}
          sx={{ mr: 1 }}
        >
          Settings
        </Button>
        <Button
          variant="outlined"
          startIcon={<AccountTreeIcon />}
          onClick={() => setLogicRulesModalOpen(true)}
          sx={{ mr: 1 }}
        >
          Logic Rules
        </Button>
        <Box sx={{ mx: 1 }}>
          <BugReportButton size="small" />
        </Box>
        <Button
          variant="contained"
          startIcon={<PublishIcon />}
          onClick={async () => {
            // Check if there are any questions before publishing
            if (questions.length === 0) {
              alert(
                "Please add at least one question before publishing the survey."
              );
              return;
            }

            const saved = await handleSave();
            if (!saved) return;

            // Update survey status to published
            try {
              const validationResponse = await fetch(
                `${getApiUrl(API_CONFIG.ENDPOINTS.LOGIC_RULES.VALIDATE)}?survey_id=${encodeURIComponent(surveyId)}`
              );
              const validationData = await validationResponse.json();
              if (!validationResponse.ok || validationData.status !== 'success') {
                throw new Error(validationData.message || 'Unable to validate survey logic');
              }
              if (!validationData.is_valid) {
                const validationErrors = [
                  ...(validationData.validation_result?.errors || []),
                  ...(validationData.validation_result?.circular_references || [])
                ];
                alert(`Survey logic is invalid and cannot be published:\n${validationErrors.map(item => item.description || item.message || JSON.stringify(item)).join('\n')}`);
                return;
              }

              const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.SURVEYS.UPDATE), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  id: surveyId,
                  user_id: user.uid,
                  name: surveyData.name,
                  theme: tempTheme || surveyData.theme,
                  layout: surveyLayout || surveyData.layout,
                  form_settings: surveyData.form_settings,
                  status: 'published'
                })
              });

              const data = await response.json();
                console.log('data',data);
              if (data.status === 'success') {
                // Update local survey data
                setSurveyData(prev => ({ ...prev, status: 'published' }));
                console.log('surveyData',surveyData);
                
                setPublishModalOpen(true);
              } else {
                throw new Error(data.message || 'Failed to publish survey');
              }
            } catch (error) {
              console.error('Error publishing survey:', error);
              alert('Failed to publish survey. Please try again.');
            }
          }}
        >
          Publish
        </Button>
      </>
    );
  };

  useEffect(() => {
    if (surveyId && user) {
      const fetchSurvey = async () => {
        try {
          const surveyUrl = `${getApiUrl(
            API_CONFIG.ENDPOINTS.SURVEYS.GET
          )}?id=${surveyId}&user_id=${user.uid}`;
          console.log("Survey API URL:", surveyUrl);

          const surveyResponse = await fetch(surveyUrl);
          console.log("Survey response status:", surveyResponse.status);

          if (!surveyResponse.ok) throw new Error("Failed to fetch survey");
          const surveyData = await surveyResponse.json();
          console.log("Survey data:", surveyData);
          if (surveyData.status === "success" && surveyData.survey) {
            setSurveyData(surveyData.survey);
            setInitialSurveyData(surveyData.survey);
            setSurveyLayout(surveyData.survey.layout || "classic");

            const questionsUrl = `${getApiUrl(
              API_CONFIG.ENDPOINTS.QUESTIONS.LIST
            )}?survey_id=${surveyId}`;
            console.log("Questions API URL:", questionsUrl);

            const questionsResponse = await fetch(questionsUrl);
            console.log("Questions response status:", questionsResponse.status);

            if (!questionsResponse.ok)
              throw new Error("Failed to fetch questions");
            const questionsData = await questionsResponse.json();
            console.log("Questions data:", questionsData);

            if (questionsData.status === "success" && questionsData.questions) {
              const formattedQuestions = questionsData.questions
                .sort((a, b) => a.order_index - b.order_index)
                .map((q) => {
                  const options = (() => {
                    // Check if it's a matrix question specifically
                    // Update the matrix options parsing in the options processing block around line 1877
                    if (q.question_type === "matrix") {
                      console.log("Processing matrix options:", q.options);
                      try {
                        let rows = [];
                        let columns = [];

                        // Case 1: When q.options is a string that needs parsing
                        if (typeof q.options === "string") {
                          try {
                            const parsed = JSON.parse(q.options);

                            // Handle the case where parsed contains an options array with id/rowvalue/columnvalue format
                            if (
                              parsed.options &&
                              Array.isArray(parsed.options)
                            ) {
                              let i = 0;
                              while (i < parsed.options.length) {
                                if (parsed.options[i] === "id") {
                                  i++; // Skip 'id'

                                  if (i < parsed.options.length) {
                                    const type = parsed.options[i];
                                    i++; // Move to the value

                                    if (
                                      (type === "rowvalue" ||
                                        type === "checkboxrow") &&
                                      i < parsed.options.length
                                    ) {
                                      rows.push(parsed.options[i]);
                                    } else if (
                                      (type === "columnvalue" ||
                                        type === "checkboxcolumn") &&
                                      i < parsed.options.length
                                    ) {
                                      columns.push(parsed.options[i]);
                                    }
                                  }
                                } else {
                                  i++; // Move to next if not starting with 'id'
                                }
                              }
                            }

                            // If we found rows and columns, use them
                            if (rows.length > 0 || columns.length > 0) {
                              console.log(
                                "Extracted matrix rows/columns from parsed options:",
                                { rows, columns }
                              );
                              return { rows, columns };
                            }

                            // Otherwise, check for direct rows/columns in the parsed object
                            if (parsed.rows && Array.isArray(parsed.rows)) {
                              rows = parsed.rows;
                            }
                            if (
                              parsed.columns &&
                              Array.isArray(parsed.columns)
                            ) {
                              columns = parsed.columns;
                            }

                            return { rows, columns };
                          } catch (e) {
                            console.error(
                              "Failed to parse matrix options string:",
                              e
                            );
                          }
                        }

                        // Case 2: When q.options is already an array
                        if (Array.isArray(q.options)) {
                          console.log(
                            "Processing flat array options:",
                            q.options
                          );
                          try {
                            let rows = [];
                            let columns = [];

                            // This is a special case when q.options is a flat array with numeric keys
                            if (q.options[0] === "id") {
                              let i = 0;
                              while (i < q.options.length) {
                                if (q.options[i] === "id") {
                                  i++; // Skip 'id'

                                  if (i < q.options.length) {
                                    const type = q.options[i];
                                    i++; // Move to the value

                                    if (i < q.options.length) {
                                      const value = q.options[i];

                                      if (
                                        type === "rowvalue" ||
                                        type === "checkboxrow"
                                      ) {
                                        rows.push(value);
                                      } else if (
                                        type === "columnvalue" ||
                                        type === "checkboxcolumn"
                                      ) {
                                        columns.push(value);
                                      }
                                    }
                                  }
                                }
                                i++;
                              }

                              if (rows.length > 0 || columns.length > 0) {
                                console.log(
                                  "Successfully extracted rows and columns from flat array:",
                                  { rows, columns }
                                );
                                return {
                                  rows:
                                    rows.length > 0
                                      ? rows
                                      : ["Row 1", "Row 2", "Row 3"],
                                  columns:
                                    columns.length > 0
                                      ? columns
                                      : ["Column 1", "Column 2", "Column 3"],
                                };
                              }
                            }
                          } catch (e) {
                            console.error(
                              "Error processing flat array matrix options:",
                              e
                            );
                          }
                        }

                        // Case 3: When q.options is an object with rows/columns
                        // Case 3: When q.options is an object with rows/columns
                        if (
                          q.options &&
                          typeof q.options === "object" &&
                          !Array.isArray(q.options)
                        ) {
                          console.log(
                            "Processing object options format:",
                            q.options
                          );

                          // Initialize rows and columns arrays
                          let rows = [];
                          let columns = [];

                          // Check for direct rows/columns properties
                          if (q.options.rows && Array.isArray(q.options.rows)) {
                            rows = q.options.rows;
                          }
                          if (
                            q.options.columns &&
                            Array.isArray(q.options.columns)
                          ) {
                            columns = q.options.columns;
                          }

                          // Check for special format with "options" property containing objects with rowvalue/checkboxrow
                          // Fix for processing flat array format in q.options.options
                          if (
                            q.options.options &&
                            Array.isArray(q.options.options)
                          ) {
                            // Try to extract rows and columns from options array
                            let i = 0;
                            while (i < q.options.options.length) {
                              if (q.options.options[i] === "id") {
                                i++; // Skip 'id'

                                if (i < q.options.options.length) {
                                  const type = q.options.options[i];
                                  i++; // Move to the value

                                  if (i < q.options.options.length) {
                                    const value = q.options.options[i];

                                    if (
                                      type === "rowvalue" ||
                                      type === "checkboxrow"
                                    ) {
                                      rows.push(value);
                                    } else if (
                                      type === "columnvalue" ||
                                      type === "checkboxcolumn"
                                    ) {
                                      columns.push(value);
                                    }
                                  }
                                }
                              }
                              i++;
                            }
                          }

                          // If we found rows and columns, use them
                          if (rows.length > 0 || columns.length > 0) {
                            console.log(
                              "Extracted matrix rows/columns from object properties:",
                              { rows, columns }
                            );
                            return {
                              rows:
                                rows.length > 0
                                  ? rows
                                  : ["Row 1", "Row 2", "Row 3"],
                              columns:
                                columns.length > 0
                                  ? columns
                                  : ["Column 1", "Column 2", "Column 3"],
                            };
                          }
                        }

                        // Use empty arrays or default values if nothing was found
                        console.warn(
                          "No matrix rows/columns found, using defaults"
                        );
                        return {
                          rows:
                            rows.length > 0
                              ? rows
                              : ["Row 1", "Row 2", "Row 3"],
                          columns:
                            columns.length > 0
                              ? columns
                              : ["Column 1", "Column 2", "Column 3"],
                        };
                      } catch (e) {
                        console.error("Matrix options processing error:", e);
                        return {
                          rows: ["Row 1", "Row 2", "Row 3"],
                          columns: ["Column 1", "Column 2", "Column 3"],
                        };
                      }
                    }

                    // For non-matrix questions, continue with your existing logic
                    if (typeof q.options === "string") {
                      try {
                        const parsed = JSON.parse(q.options || "{}");
                        // Check for both formats: {options: [...]} or {choices: [...]}
                        if (parsed.options && Array.isArray(parsed.options)) {
                          return parsed.options;
                        } else if (
                          parsed.choices &&
                          Array.isArray(parsed.choices)
                        ) {
                          return parsed.choices;
                        } else if (Array.isArray(parsed)) {
                          // Handle case where the string is a direct JSON array
                          return parsed;
                        }
                        return [];
                      } catch (e) {
                        console.error("Failed to parse options string:", e);
                        return [];
                      }
                    }
                    // Case 2: If options is already an array, use it directly
                    else if (Array.isArray(q.options)) {
                      return q.options;
                    }
                    // Case 3: If options is an object with options/choices property
                    else if (q.options && typeof q.options === "object") {
                      if (
                        q.options.options &&
                        Array.isArray(q.options.options)
                      ) {
                        return q.options.options;
                      } else if (
                        q.options.choices &&
                        Array.isArray(q.options.choices)
                      ) {
                        return q.options.choices;
                      }
                    }
                    // Default empty array
                    return [];
                  })();

                  // console.log('Processed options for question:', q.id, options);

                  // Parse options object if it exists
                  let parsedOptions = {};
                  if (typeof q.options === "string") {
                    try {
                      parsedOptions = JSON.parse(q.options || "{}");
                    } catch (e) {
                      console.error("Failed to parse options JSON:", e);
                    }
                  } else if (q.options && typeof q.options === "object") {
                    parsedOptions = q.options;
                  }

                  return {
                    id: q.id,
                    type: q.question_type,
                    title: q.question_text,
                    required: Boolean(q.required),
                    // Store the full options object for UI configuration
                    options: parsedOptions,
                    // Store the choices array separately for backward compatibility
                    choices:
                      q.question_type === "matrix"
                        ? []
                        : parsedOptions.choices || options,
                    // Keep these properties for backward compatibility
                    rows:
                      q.question_type === "matrix"
                        ? options.rows
                        : parsedOptions.rows || [],
                    columns:
                      q.question_type === "matrix"
                        ? options.columns
                        : parsedOptions.columns || [],
                    orderIndex: q.order_index,
                    // Use values from options object with fallbacks to direct properties
                    caption: parsedOptions.caption,
                    placeholder: parsedOptions.placeholder,
                    defaultValue: parsedOptions.defaultValue,
                    errorMessage: parsedOptions.errorMessage,
                    validation: parsedOptions.validation,
                    minLength: parsedOptions.minLength,
                    maxLength: parsedOptions.maxLength,
                    minValue: parsedOptions.minValue,
                    maxValue: parsedOptions.maxValue,
                    allowOther: parsedOptions.allowOther,
                    randomize: options.randomize,
                    showValidation: options.showValidation,
                    textFormat: options.textFormat,
                    config: options.config,
                    subfields: options.subfields || [],
                    enabledFields: options.enabledFields || {},
                    allowMultipleSelection: options.allowMultipleSelection,
                    maxFileSize: options.maxFileSize,
                    allowedFileTypes: options.allowedFileTypes || [],
                  };
                });
              // console.log('Formatted questions:', formattedQuestions);
              setQuestions(formattedQuestions);
              setInitialQuestions(formattedQuestions);
            } else {
              // console.log('No questions found or error in questions data');
            }
          } else {
            throw new Error(surveyData.message || "Failed to fetch survey");
          }
        } catch (error) {
          console.error("Error fetching survey:", error);
          setError(error.message);
        } finally {
          setLoading(false);
        }
      };
      fetchSurvey();
    }
  }, [surveyId, user]);



  const scrollToQuestion = (questionIndex) => {
    setTimeout(() => {
      const questionElement = document.querySelector(
        `[data-question-index="${questionIndex}"]`
      );
      if (questionElement) {
        questionElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
  };

  const handleAddQuestion = (type, sidebarContainerRef = null) => {
    const newQuestion = {
      type,
      title: "",
      required: false,
      choices: questionTypes[type].hasOptions ? [] : undefined,
      options: {}, // Initialize empty options object for UI configuration
      ...(type === "file-upload" && {
        maxFileSize: 2,
        allowedFileTypes: [],
        errorMessage: "Invalid file",
      }),
      ...(questionTypes[type].hasRows && { rows: type === "matrix" ? ["Row 1", "Row 2", "Row 3"] : [] }),
      ...(questionTypes[type].hasColumns && { columns: type === "matrix" ? ["Column 1", "Column 2", "Column 3"] : [] }),
      ...(type === "address" && {
        subfields: ["street", "city", "state", "zip", "country"],
        enabledFields: {
          street: true,
          city: true,
          state: true,
          zip: true,
          country: true,
        },
      }),
    };

    setQuestions((prev) => {
      const newQuestions = [...prev];
      if (selectedQuestion !== null) {
        newQuestions.splice(selectedQuestion + 1, 0, newQuestion);
      } else {
        newQuestions.push(newQuestion);
      }
      return newQuestions;
    });

    if (sidebarContainerRef?.current) {
      sidebarContainerRef.current.style.scrollBehavior = "auto";
      sidebarContainerRef.current.scrollTop = sidebarScrollPosition.current;
    }

    setTimeout(() => {
      const newIndex =
        selectedQuestion !== null ? selectedQuestion + 1 : questions.length;
      setSelectedQuestion(newIndex);
      scrollToQuestion(newIndex);
    }, 100);
  };

  const handleQuestionChange = (index, field, value) => {
    setQuestions((prevQuestions) => {
      const updatedQuestions = [...prevQuestions];
      const currentQuestion = updatedQuestions[index];

      // Special handling for options object
      if (field === "options") {
        // If options is being set directly, update the entire options object
        updatedQuestions[index] = {
          ...updatedQuestions[index],
          options: value,
          hasChanges: true,
        };
      } else if (
        [
          "caption",
          "placeholder",
          "defaultValue",
          "errorMessage",
          "minLength",
          "maxLength",
          "minValue",
          "maxValue",
        ].includes(field)
      ) {
        // Special handling for image-choice questions
        if (currentQuestion.type === 'image-choice') {
          // For image-choice questions, store configuration directly on the question
          updatedQuestions[index] = {
            ...updatedQuestions[index],
            [field]: value,
            hasChanges: true,
          };
        } else {
          // Store UI-related configuration in the options object for other question types
          updatedQuestions[index] = {
            ...updatedQuestions[index],
            options: {
              ...(updatedQuestions[index].options || {}),
              [field]: value,
            },
            hasChanges: true,
          };
        }
      } else {
        // For all other fields, update directly
        updatedQuestions[index] = {
          ...updatedQuestions[index],
          [field]: value,
          hasChanges: true,
        };
      }

      return updatedQuestions;
    });
  };

  

  const handleQuestionDelete = async (index) => {
    try {
      const question = questions[index];

      // If the question has an ID, delete it from the backend
      if (question.id) {
        const response = await fetch(
          getApiUrl(API_CONFIG.ENDPOINTS.QUESTIONS.DELETE),
          {
            method: "POST",
            credentials: "omit",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              id: question.id,
              survey_id: surveyId,
              user_id: user.uid,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(errorData || "Failed to delete question");
        }

        const result = await response.json();
        if (result.status !== "success") {
          throw new Error(result.message || "Failed to delete question");
        }
      }

      // Remove the question from the local state
      const updatedQuestions = questions.filter((_, i) => i !== index);

      // Recalculate and update the order_index for the remaining questions
      const reorderedQuestions = updatedQuestions.map((q, i) => ({
        ...q,
        order_index: i, // Update order_index to match the new position
        hasChanges: true, // Mark as changed to update the backend
      }));

      setQuestions(reorderedQuestions);

      // Update the backend with the new order_index values
      const questionsToUpdate = reorderedQuestions
        .filter((q) => q.id) // Only update questions that exist in the backend
        .map((q) => ({
          id: q.id,
          survey_id: surveyId,
          question_type: q.type || "", // Ensure question_type is included
          question_text: q.title || "", // Ensure question_text is included
          order_index: q.order_index,
          required: q.required || false, // Include required field
          options: q.options || [], // Include options if applicable
        }));

      if (questionsToUpdate.length > 0) {
        const response = await fetch(
          getApiUrl(API_CONFIG.ENDPOINTS.QUESTIONS.BULK_UPDATE),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              questions: questionsToUpdate,
              survey_id: surveyId,
              user_id: user.uid,
            }),
          }
        );

        const result = await response.json();
        if (result.status !== "success") {
          throw new Error(result.message || "Failed to update question order");
        }
      }

      setSuccess("Question deleted and order updated successfully");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting question:", error);
      setError(error.message || "Failed to delete question");
      setSnackbarOpen(true);
    }
  };

  // Frontend validation function
  const validateQuestions = (questionsToValidate) => {
    const validationErrors = {};

    questionsToValidate.forEach((question, index) => {
      const errors = [];

      // Check if question title is empty
      if (!question.title || question.title.trim() === "") {
        errors.push("Please enter a question title");
      }

      // Check specific validation based on question type
      if (
        question.type === "multiple_choice" ||
        question.type === "multiple-choice" ||
        question.type === "checkboxes" ||
        question.type === "dropdown"
      ) {
        if (!question.choices || question.choices.length === 0) {
          errors.push("Please add at least one option");
        } else {
          // Check if any option is empty
          const hasEmptyOptions = question.choices.some((option) =>
            typeof option === "string"
              ? option.trim() === ""
              : !option.label || option.label.trim() === ""
          );
          if (hasEmptyOptions) {
            errors.push("All options must have text");
          }
        }
      }

      if (question.type === "matrix") {
        if (!question.rows || question.rows.length === 0) {
          errors.push("Please add at least one row");
        } else {
          // Check if all rows have names (default names like "Row 1" are valid)
          const hasEmptyRows = question.rows.some((row) => !row || row.trim() === "");
          if (hasEmptyRows) {
            errors.push("All rows must have names");
          }
        }
        if (!question.columns || question.columns.length === 0) {
          errors.push("Please add at least one column");
        } else {
          // Check if all columns have names (default names like "Column 1" are valid)
          const hasEmptyColumns = question.columns.some((col) => !col || col.trim() === "");
          if (hasEmptyColumns) {
            errors.push("All columns must have names");
          }
        }
      }

      if (question.type === "image-choice") {
        const imageOptions = getImageChoiceOptions(question.options);
        if (!imageOptions) {
          errors.push("Image options must be a list or an object containing an options list");
        } else if (imageOptions.length === 0) {
          errors.push("Please add at least one image option");
        } else {
          const missingImages = imageOptions.some(
            (option) => !option || typeof option !== "object" || (!option.imageUrl && !option.tempId)
          );
          if (missingImages) {
            errors.push("All image options must have an image");
          }
        }
      }

      // Add validation errors to the map if any exist
      if (errors.length > 0) {
        validationErrors[index] = {
          errors,
          title: question.title || `Question ${index + 1}`,
          id: question.id,
        };
      }
    });

    return validationErrors;
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSnackbarOpen(false);

      // Frontend validation first
      const frontendValidationErrors = validateQuestions(questions);

      if (Object.keys(frontendValidationErrors).length > 0) {
        setQuestionErrors(frontendValidationErrors);

        const errorCount = Object.keys(frontendValidationErrors).length;
        const errorMessages = Object.values(frontendValidationErrors)
          .map((err) => `${err.title}: ${err.errors.join(", ")}`)
          .join("\n");

        setError(
          `${errorCount} question(s) have validation errors:\n${errorMessages}`
        );
        setSnackbarOpen(true);
        return false; // Stop execution here
      }

      // Clear any previous validation errors
      setQuestionErrors({});

      const imageUploads = Object.entries(tempImages).map(
        async ([tempId, imageData]) => {
          try {
            // Create FormData with the blob
            const formData = new FormData();
            // Create a blob from the File object
            const blob = new Blob([imageData.file], {
              type: imageData.file.type,
            });
            formData.append("file", blob, imageData.file.name); // Add proper file extension based on type
            formData.append("survey_id", surveyId);
            formData.append("user_id", user.uid);
            formData.append("question_id", imageData.questionId || "");

            // Log for debugging
            // console.log('Uploading file:', {
            //   fileName: imageData.file.name,
            //   fileType: imageData.file.type,
            //   fileSize: imageData.file.size
            // });

            const response = await fetch(
              `${API_CONFIG.BASE_URL}/upload_doc.php`,
              {
                method: "POST",
                body: formData,
              }
            );

            if (!response.ok) {
              throw new Error(`Upload failed with status ${response.status}`);
            }

            const result = await response.json();
            if (!result.success) {
              throw new Error(`Failed to upload image: ${result.message}`);
            }

            return {
              tempId,
              fileUrl: result.file.url, // Use the actual URL from server
              fileId: result.file.id,
            };
          } catch (error) {
            console.error("Image upload error:", error);
            throw error;
          }
        }
      );
      // Wait for all image uploads to complete
      const uploadedImages = await Promise.all(imageUploads);

      const questionsWithImages = questions.map((question) => {
        if (question.type === "image-choice") {
          const options = question.options.map((option) => {
            if (option.tempId) {
              const uploadedImage = uploadedImages.find(
                (img) => img.tempId === option.tempId
              );
              if (uploadedImage) {
                return {
                  ...option,
                  imageUrl: uploadedImage.fileUrl,
                  fileId: uploadedImage.fileId,
                  tempId: undefined,
                };
              }
            }
            return option;
          });
          return { ...question, options };
        }
        return question;
      });

      // Only send new or changed questions
      const newQuestions = questionsWithImages
        .filter((q) => !q.id)
        .map((q, index) => {
          // Prepare the options object that will contain all UI-related configuration
          const optionsObject = q.type === "image-choice"
            ? {
              // Handle image-choice questions - store image array and get config from direct properties
              options: q.options.map((opt) => ({
                imageUrl: opt.imageUrl || "",
                label: opt.label || "",
                showDescription: Boolean(opt.showDescription),
                description: opt.description || "",
                fileId: opt.fileId || null,
              })),
              allowMultipleSelection: q.allowMultipleSelection || false,
              caption: q.caption || "",
              placeholder: q.placeholder || "",
              defaultValue: q.defaultValue || "",
              errorMessage: q.errorMessage || "",
              validation: q.validation || "",
            }
            : {
              // Handle all other question types
              // Include choices/options array for choice-based questions
              choices:
               q.type === "multiple_choice" ||
               q.type === "ranking" ||
                q.type === "multiple-choice" ||
                  q.type === "checkboxes" ||
                  q.type === "dropdown"
                  ? q.choices || []
                  : undefined,

              // Include UI configuration fields
              caption:
                q.options?.caption !== undefined ? q.options.caption : q.caption,
              placeholder:
                q.options?.placeholder !== undefined
                  ? q.options.placeholder
                  : q.placeholder,
              defaultValue:
                q.options?.defaultValue !== undefined
                  ? q.options.defaultValue
                  : q.defaultValue,
              errorMessage:
                q.options?.errorMessage !== undefined
                  ? q.options.errorMessage
                  : q.errorMessage,
              minLength:
                q.options?.minLength !== undefined
                  ? q.options.minLength
                  : q.minLength,
              maxLength:
                q.options?.maxLength !== undefined
                  ? q.options.maxLength
                  : q.maxLength,
              minValue:
                q.options?.minValue !== undefined
                  ? q.options.minValue
                  : q.minValue,
              maxValue:
                q.options?.maxValue !== undefined
                  ? q.options.maxValue
                  : q.maxValue,

              // Include validation settings
              validation:
                q.options?.validation !== undefined
                  ? q.options.validation
                  : q.validation,

              // Include matrix-specific settings
              rows: q.type === "matrix" ? q.rows || [] : undefined,
              columns: q.type === "matrix" ? q.columns || [] : undefined,

              // Include configuration objects
              config:
                q.type === "range-scale"
                  ? {
                    min: q.config?.min,
                    max: q.config?.max,
                    middle: q.config?.middle,
                    step: q.config?.step || 1,
                    showTicks: q.config?.showTicks || false,
                    labels: {
                      start: q.config?.labels?.start || "Low",
                      middle: q.config?.labels?.middle || "Medium",
                      end: q.config?.labels?.end || "High",
                    },
                  }
                  : q.config,

              // Include other settings
              allowOther: q.allowOther,
              allowMultipleSelection: q.allowMultipleSelection || false,
              randomize: q.randomize,
              showValidation: q.showValidation,
              textFormat: q.textFormat,
            };

          return {
            survey_id: surveyId,
            question_type: q.type,
            question_text: q.title,
            required: Boolean(q.required),
            order_index: questionsWithImages.findIndex(
              (question) => question === q
            ),
            options: optionsObject,
          };
        });

      const modifiedExistingQuestions = questionsWithImages
        .filter((q) => q.id && q.hasChanges)
        .map((q) => {
          // Prepare the options object that will contain all UI-related configuration
          const optionsObject =
            q.type === "matrix"
              ? {
                rows: q.rows || [],
                columns: q.columns || [],
                allowMultipleSelection: q.allowMultipleSelection || false,
                caption:
                  q.options?.caption !== undefined
                    ? q.options.caption
                    : q.caption,
                placeholder:
                  q.options?.placeholder !== undefined
                    ? q.options.placeholder
                    : q.placeholder,
                defaultValue:
                  q.options?.defaultValue !== undefined
                    ? q.options.defaultValue
                    : q.defaultValue,
                errorMessage:
                  q.options?.errorMessage !== undefined
                    ? q.options.errorMessage
                    : q.errorMessage,
                validation:
                  q.options?.validation !== undefined
                    ? q.options.validation
                    : q.validation,
              }
              : q.type === "image-choice"
                ? {
                  options: q.options.map((opt) => ({
                    imageUrl: opt.imageUrl || "",
                    label: opt.label || "",
                    showDescription: Boolean(opt.showDescription),
                    description: opt.description || "",
                    fileId: opt.fileId || null,
                  })),
                  allowMultipleSelection: q.allowMultipleSelection || false,
                  caption: q.caption || "",
                  placeholder: q.placeholder || "",
                  defaultValue: q.defaultValue || "",
                  errorMessage: q.errorMessage || "",
                  validation: q.validation || "",
                }
                : {
                  choices: q.choices || [],
                  rows: q.rows || [],
                  columns: q.columns || [],
                  caption:
                    q.options?.caption !== undefined
                      ? q.options.caption
                      : q.caption,
                  placeholder:
                    q.options?.placeholder !== undefined
                      ? q.options.placeholder
                      : q.placeholder,
                  defaultValue:
                    q.options?.defaultValue !== undefined
                      ? q.options.defaultValue
                      : q.defaultValue,
                  errorMessage:
                    q.options?.errorMessage !== undefined
                      ? q.options.errorMessage
                      : q.errorMessage,
                  minLength:
                    q.options?.minLength !== undefined
                      ? q.options.minLength
                      : q.minLength,
                  maxLength:
                    q.options?.maxLength !== undefined
                      ? q.options.maxLength
                      : q.maxLength,
                  minValue:
                    q.options?.minValue !== undefined
                      ? q.options.minValue
                      : q.minValue,
                  maxValue:
                    q.options?.maxValue !== undefined
                      ? q.options.maxValue
                      : q.maxValue,
                  validation:
                    q.options?.validation !== undefined
                      ? q.options.validation
                      : q.validation,
                  config: q.config,
                  allowOther: q.allowOther,
                  allowMultipleSelection: q.allowMultipleSelection || false,
                  randomize: q.randomize,
                  showValidation: q.showValidation,
                  textFormat: q.textFormat,
                };

          return {
            id: q.id,
            survey_id: surveyId,
            question_type: q.type,
            question_text: q.title,
            required: Boolean(q.required),
            order_index: questionsWithImages.findIndex(
              (question) => question === q
            ),
            options: JSON.stringify(optionsObject),
          };
        });

      // Only send if there are new or changed questions
      const questionsToSend = [...newQuestions, ...modifiedExistingQuestions];

      if (questionsToSend.length > 0) {
        const response = await fetch(
          getApiUrl(API_CONFIG.ENDPOINTS.QUESTIONS.BULK_UPDATE),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              questions: questionsToSend,
            }),
          }
        );
        const result = await response.json();

        if (result.status === "success") {
          const updatedQuestions = [...questions];
          if (result.processedQuestions) {
            Object.keys(result.processedQuestions).forEach((index) => {
              const processedQuestion = result.processedQuestions[index];
              const questionIndex = parseInt(index);
              if (questionIndex < updatedQuestions.length) {
                if (
                  processedQuestion.status === "created" &&
                  processedQuestion.id
                ) {
                  updatedQuestions[questionIndex] = {
                    ...updatedQuestions[questionIndex],
                    id: processedQuestion.id,
                    hasChanges: false,
                  };
                } else if (
                  processedQuestion.status === "updated" ||
                  processedQuestion.status === "no_change"
                ) {
                  updatedQuestions[questionIndex] = {
                    ...updatedQuestions[questionIndex],
                    hasChanges: false,
                  };
                }
              }
            });
          }
          setQuestions(updatedQuestions);
          setSuccess("Survey saved successfully");
          setTempImages({});
           try {
           if (user) setClarityHost(user);
            sendClarityEvent("survey_saved", {
              surveyId,
              userId: user?.uid || null,
              questionCount: updatedQuestions.length,
              timestamp: new Date().toISOString(),
            });
          } catch (e) {
            console.warn("Clarity error while reporting survey save", e);
          }
        } else if (result.status === "partial_success") {
          const updatedQuestions = [...questions];
          const backendErrors = {};
          if (result.processedQuestions) {
            Object.keys(result.processedQuestions).forEach((index) => {
              const processedQuestion = result.processedQuestions[index];
              const questionIndex = parseInt(index);
              if (questionIndex < updatedQuestions.length) {
                if (
                  processedQuestion.status === "created" &&
                  processedQuestion.id
                ) {
                  updatedQuestions[questionIndex] = {
                    ...updatedQuestions[questionIndex],
                    id: processedQuestion.id,
                    hasChanges: false,
                  };
                } else if (
                  processedQuestion.status === "updated" ||
                  processedQuestion.status === "no_change"
                ) {
                  updatedQuestions[questionIndex] = {
                    ...updatedQuestions[questionIndex],
                    hasChanges: false,
                  };
                } else if (processedQuestion.status === "error") {
                  backendErrors[questionIndex] = {
                    errors: [processedQuestion.error || "Unknown error"],
                    title:
                      updatedQuestions[questionIndex].title ||
                      `Question ${questionIndex + 1}`,
                    id: updatedQuestions[questionIndex].id,
                  };
                }
              }
            });
          }
          setQuestions(updatedQuestions);
          if (Object.keys(backendErrors).length > 0) {
            setQuestionErrors(backendErrors);
            const errorCount = Object.keys(backendErrors).length;
            const errorMessages = Object.values(backendErrors)
              .map((err) => `${err.title}: ${err.errors.join(", ")}`)
              .join("\n");
            setError(
              `${errorCount} question(s) could not be saved:\n${errorMessages}`
            );
          } else {
            setSuccess("Survey saved successfully");

          }
          setTempImages({});
           try {
           if (user) setClarityHost(user);
            sendClarityEvent("survey_saved", {
              surveyId,
              userId: user?.uid || null,
              questionCount: updatedQuestions.length,
              timestamp: new Date().toISOString(),
            });
          } catch (e) {
            console.warn("Clarity error while reporting survey save", e);
          }
        } else {
          throw new Error(result.message || "Failed to save questions");
        }
      } else {
        setSuccess("No changes to save");
      }

      setSnackbarOpen(true);
      return true;
    } catch (error) {
      console.error("Save error:", error);
      setError(error.message);
      setSnackbarOpen(true);
      return false; // Early return to prevent further execution
    } finally {
      setIsSaving(false);
    }
  };

  const handleQuestionSelect = (index) => {
    setSelectedQuestion(index);
  };

  const handleQuestionOptionsChange = (field, value) => {
    if (selectedQuestion !== null) {
      const currentQuestion = questions[selectedQuestion];

      // For UI configuration fields, we need to update both the options object AND the direct properties
      // for immediate preview updates
      if (
        [
          "caption",
          "placeholder",
          "defaultValue",
          "errorMessage",
          "minLength",
          "maxLength",
          "minValue",
          "maxValue",
        ].includes(field)
      ) {
        // Update both the direct property (for immediate preview) and options object (for saving)
        setQuestions((prevQuestions) => {
          const updatedQuestions = [...prevQuestions];
          const currentQ = updatedQuestions[selectedQuestion];
          
          // Update direct property for immediate preview
          updatedQuestions[selectedQuestion] = {
            ...currentQ,
            [field]: value,
            hasChanges: true,
          };

          // Also update options object for proper saving
          if (currentQ.type !== 'image-choice') {
            updatedQuestions[selectedQuestion].options = {
              ...(currentQ.options || {}),
              [field]: value,
            };
          }

          return updatedQuestions;
        });
      } else if (field === "validation") {
        // Handle validation object updates
        setQuestions((prevQuestions) => {
          const updatedQuestions = [...prevQuestions];
          const currentQ = updatedQuestions[selectedQuestion];
          
          const updatedValidation = {
            ...(currentQ.validation || {}),
            ...value,
          };

          updatedQuestions[selectedQuestion] = {
            ...currentQ,
            validation: updatedValidation,
            hasChanges: true,
          };

          // Also update options object for proper saving
          if (currentQ.type !== 'image-choice') {
            updatedQuestions[selectedQuestion].options = {
              ...(currentQ.options || {}),
              validation: updatedValidation,
            };
          }

          return updatedQuestions;
        });
      } else if (field === "config") {
        // Handle config object updates
        setQuestions((prevQuestions) => {
          const updatedQuestions = [...prevQuestions];
          const currentQ = updatedQuestions[selectedQuestion];
          
          updatedQuestions[selectedQuestion] = {
            ...currentQ,
            config: value,
            hasChanges: true,
          };

          // Also update options object for proper saving
          if (currentQ.type !== 'image-choice') {
            updatedQuestions[selectedQuestion].options = {
              ...(currentQ.options || {}),
              config: value,
            };
          }

          return updatedQuestions;
        });
      } else {
        // For all other fields, update directly
        handleQuestionChange(selectedQuestion, field, value);
      }
    }
  };

  const handlePreviewOpen = () => setPreviewOpen(true);
  const handlePreviewClose = () => setPreviewOpen(false);

  const handleQuestionDragStart = (e, index) => {
    setDraggedQuestionIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.classList.add("dragging");

    // Keep the dragged card in the layout as a placeholder without a floating ghost.
    const dragImage = document.createElement("canvas");
    dragImage.width = 1;
    dragImage.height = 1;
    e.dataTransfer.setDragImage(dragImage, 0, 0);

    // Add visual feedback to the questions area with smooth transition
    const questionsArea = document.querySelector(".questions-area");
    if (questionsArea) {
      questionsArea.classList.add("dragging-active");
    }

    console.log(
      `Started dragging question ${index}: ${questions[index]?.title || "Untitled"
      }`
    );
  };

  const handleQuestionDragEnd = (e) => {
    e.currentTarget.classList.remove("dragging");
    setDraggedQuestionIndex(null);
    setDropTargetIndex(null);

    // Clean up visual feedback with smooth transitions
    const questionsArea = document.querySelector(".questions-area");
    if (questionsArea) {
      questionsArea.classList.remove("dragging-active");
    }

    // Remove all drop zone indicators and hover effects
    document
      .querySelectorAll(".drop-zone-indicator")
      .forEach((el) => el.remove());
    document
      .querySelectorAll(".drag-over")
      .forEach((el) => el.classList.remove("drag-over"));

    console.log("Drag ended, cleaning up visual feedback");
  };

  const handleQuestionDragLeave = (e) => {
    // Only remove indicators if we're leaving the current target entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      const indicators = e.currentTarget.querySelectorAll(
        ".drop-zone-indicator"
      );
      indicators.forEach((indicator) => indicator.remove());
      e.currentTarget.classList.remove("drag-over");
    }
  };

  const handleQuestionDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (draggedQuestionIndex === null || draggedQuestionIndex === index) {
      return;
    }

    // Calculate drop position based on mouse position within the element
    const currentTarget = e.currentTarget;
    const rect = currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const isTopHalf = y < rect.height / 2;

    // Determine the actual drop index based on position and drag direction
    let actualDropIndex;
    if (draggedQuestionIndex < index) {
      // Dragging down: drop after the target if in bottom half, before if in top half
      actualDropIndex = isTopHalf ? index : index + 1;
    } else {
      // Dragging up: drop before the target if in top half, after if in bottom half
      actualDropIndex = isTopHalf ? index : index + 1;
    }

    // Throttle the drop target updates to improve performance
    if (dropTargetIndex !== actualDropIndex) {
      setDropTargetIndex(actualDropIndex);
    }

    // Remove previous drop indicators
    document
      .querySelectorAll(".drop-zone-indicator")
      .forEach((el) => el.remove());

    // Add enhanced drop zone indicator
    const indicator = document.createElement("div");
    indicator.className = "drop-zone-indicator";
    indicator.style.cssText = `
    position: absolute;
    left: 8px;
    right: 8px;
    height: 4px;
    background: linear-gradient(90deg, #358c7c, #4ade80);
    border-radius: 2px;
    z-index: 1000;
    box-shadow: 0 0 12px rgba(53, 140, 124, 0.6);
    ${isTopHalf ? "top: -2px;" : "bottom: -2px;"}
    animation: dropZonePulse 1.5s infinite;
  `;

    currentTarget.style.position = "relative";
    currentTarget.appendChild(indicator);

    // Add subtle hover effect to the target
    currentTarget.classList.add("drag-over");

    console.log(
      `Drag over question ${index}, drop would be at index ${actualDropIndex}`
    );
  };

  const handleQuestionDrop = async (e, targetIndex) => {
    e.preventDefault();

    if (draggedQuestionIndex === null) {
      return;
    }

    console.log(
      `Drag drop: from ${draggedQuestionIndex} to target ${targetIndex}`
    );

    // Calculate the actual drop position based on mouse position
    const currentTarget = e.currentTarget;
    const rect = currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const isTopHalf = y < rect.height / 2;
    // Simplified drop index calculation
    let dropIndex;
    if (isTopHalf) {
      dropIndex = targetIndex;
    } else {
      dropIndex = targetIndex + 1;
    }

    // Ensure drop index is within bounds
    dropIndex = Math.max(0, Math.min(questions.length, dropIndex));

    // If dropping in the same position, return early
    if (
      draggedQuestionIndex === dropIndex ||
      (draggedQuestionIndex === dropIndex - 1 &&
        dropIndex > draggedQuestionIndex)
    ) {
      console.log("No movement needed - dropping in same position");
      setDraggedQuestionIndex(null);
      setDropTargetIndex(null);
      document
        .querySelectorAll(".drop-zone-indicator")
        .forEach((el) => el.remove());
      return;
    }

    console.log(
      `Final drop calculation: moving from ${draggedQuestionIndex} to ${dropIndex}`
    );

    // Store original questions for rollback on error
    const originalQuestions = [...questions];

    // Perform the array manipulation
    const newQuestions = [...questions];
    const [draggedQuestion] = newQuestions.splice(draggedQuestionIndex, 1);

    // Adjust drop index if we removed an element before it
    const adjustedDropIndex =
      dropIndex > draggedQuestionIndex ? dropIndex - 1 : dropIndex;
    newQuestions.splice(adjustedDropIndex, 0, draggedQuestion);

    console.log(
      "New question order:",
      newQuestions.map(
        (q, i) => `${i}: ${q.title || "Untitled"} (id: ${q.id || "new"})`
      )
    );

    // Update questions order_index for all questions
    const updatedQuestions = newQuestions.map((q, idx) => ({
      ...q,
      order_index: idx,
      hasChanges: true,
    }));

    setQuestions(updatedQuestions);
    setDraggedQuestionIndex(null);
    setDropTargetIndex(null);
    // Clean up visual indicators
    document
      .querySelectorAll(".drop-zone-indicator")
      .forEach((el) => el.remove());

    // Show loading indicator during position update
    if (isUpdatingPositions) {
      const loadingIndicator = document.createElement("div");
      loadingIndicator.className = "position-update-loading";
      loadingIndicator.innerHTML = `
      <div style="
        position: fixed;
        top: 80px;
        right: 20px;
        background: rgba(53, 140, 124, 0.9);
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        gap: 8px;
        z-index: 1000;
        font-size: 14px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      ">
        <div class="loading-spinner" style="
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 1s linear infinite;
        "></div>
        Updating question order...
      </div>
    `;
      document.body.appendChild(loadingIndicator);

      // Add CSS animation if not exists
      if (!document.getElementById("loading-spinner-style")) {
        const style = document.createElement("style");
        style.id = "loading-spinner-style";
        style.textContent = `
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `;
        document.head.appendChild(style);
      }
    }

    try {
      setIsUpdatingPositions(true);
      // Use the optimized position-only API for drag-and-drop operations
      const questionsToUpdate = updatedQuestions
        .filter((q) => q.id) // Only existing questions with IDs
        .map((q, idx) => ({
          id: q.id,
          survey_id: surveyId, // Include survey_id in each question object
          order_index: idx,
        }));

      if (questionsToUpdate.length > 0) {
        console.log(
          "Sending position-only update request:",
          questionsToUpdate.length,
          "questions"
        );
        console.log(
          "Questions to update:",
          questionsToUpdate.map((q) => ({
            id: q.id,
            survey_id: q.survey_id,
            order_index: q.order_index,
          }))
        );

        const response = await fetch(
          getApiUrl(API_CONFIG.ENDPOINTS.QUESTIONS.UPDATE_POSITIONS),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              questions: questionsToUpdate,
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            "Position update response error:",
            response.status,
            errorText
          );
          throw new Error(
            `HTTP ${response.status}: Failed to update question positions`
          );
        }

        const result = await response.json();
        console.log("Position update response:", result);

        if (result.status !== "success") {
          throw new Error(
            result.message || "Failed to update question positions"
          );
        } // Mark questions as saved and provide user feedback
        setQuestions((prev) =>
          prev.map((q) => ({
            ...q,
            hasChanges: false,
          }))
        );

        setSuccess("Question order updated successfully");
        setSnackbarOpen(true);
      } else {
        console.log("No existing questions to update positions for");
      }
    } catch (error) {
      console.error("Error updating question positions:", error);

      // Rollback UI changes on error
      setQuestions(originalQuestions);

      // Provide more specific error messages
      let errorMessage = "Failed to update question order";
      if (error.message.includes("HTTP 500")) {
        errorMessage =
          "Server error while updating question order. Please try again.";
      } else if (error.message.includes("HTTP 404")) {
        errorMessage =
          "Question update endpoint not found. Please check your configuration.";
      } else if (error.message.includes("Failed to fetch")) {
        errorMessage =
          "Network error. Please check your internet connection and try again.";
      } else if (error.message) {
        errorMessage = `Failed to update question order: ${error.message}`;
      }

      setError(errorMessage);
      setSnackbarOpen(true);
      // Clean up drag state on error
      setDraggedQuestionIndex(null);
      setDropTargetIndex(null);
      document
        .querySelectorAll(".drop-zone-indicator")
        .forEach((el) => el.remove());
      document
        .querySelectorAll(".drag-over")
        .forEach((el) => el.classList.remove("drag-over"));
    } finally {
      setIsUpdatingPositions(false);

      // Remove loading indicator
      document
        .querySelectorAll(".position-update-loading")
        .forEach((el) => el.remove());
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const data = e.dataTransfer.getData("text/plain");
    if (!data) return;

    try {
      handleAddQuestion(data);
    } catch (error) {
      console.error("Error handling drop:", error);
    }
  };

  const icons = {
    "short-answer": <BsInputCursor />,
    "text": <BsInputCursor />,
    "multiple-choice": <BsListCheck />,
    "multiple_choice": <BsListCheck />,
    email: <BsEnvelope />,
    text: <BsTextParagraph />,
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }


  const decodeHtmlEntities = (text) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;
    return textarea.value;
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(null);
    setSnackbarOpen(false);
  };

  const handleQuestionTypeDragStart = (e, type) => {
    e.preventDefault();
    e.stopPropagation();

    e.dataTransfer.setData("text/plain", type);
    e.dataTransfer.setData("application/x-question-type", type);
    e.dataTransfer.effectAllowed = "copy";

    const preview = document.createElement("div");
    preview.className = "drag-preview";
    preview.innerHTML = `
    <span class="question-type-icon">${questionTypes[type].icon.type}</span>
    <span>${questionTypes[type].label}</span>
  `;
    document.body.appendChild(preview);
    e.dataTransfer.setDragImage(preview, 0, 0);

    setDraggedType(type);

    requestAnimationFrame(() => {
      document.body.removeChild(preview);
    });
  };

  const handleDragEnd = () => {
    setDropTarget(null);
    setDraggedType(null);
  };

  const SidebarQuestionTypes = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const questionTypesContainerRef = useRef(null);

    useEffect(() => {
      const container = questionTypesContainerRef.current;
      if (!container) return;

      const handleScroll = () => {
        sidebarScrollPosition.current = container.scrollTop;
      };

      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }, []);

    useLayoutEffect(() => {
      const container = questionTypesContainerRef.current;
      if (container && sidebarScrollPosition.current) {
        container.scrollTop = sidebarScrollPosition.current;
      }
    });

    const handleQuestionTypeClick = (type, e) => {
      e.preventDefault();
      e.stopPropagation();

      const container = questionTypesContainerRef.current;
      const currentScroll = container.scrollTop;

      container.style.scrollBehavior = "auto";

      handleAddQuestion(type, questionTypesContainerRef);

      requestAnimationFrame(() => {
        container.scrollTop = currentScroll;
        container.style.scrollBehavior = "smooth";
      });
    };

    return (
      <div className="sidebar" id="questionTypes">
        <Box
          sx={{
            p: 2,
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 1,
          }}
        >
          <TextField
            // className="text-8xl font-bold mb-4"
            size="small"
            fullWidth
            placeholder="Search questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <div
          className="question-types-container"
          ref={(el) => {
            questionTypesContainerRef.current = el;
            sidebarRef.current = el;
          }}
          style={{
            overscrollBehavior: "contain",
            position: "relative",
            height: "calc(100vh - 144px)",
            overflowY: "auto",
            scrollBehavior: "auto",
          }}
        >
          <List sx={{ p: 2 }}>
            {Object.entries(questionTypes)
              .filter(([key, value]) =>
                value.label.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map(([key, value]) => (
                <ListItem
                  disablePadding
                  key={key}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleQuestionTypeClick(key, e);
                  }}
                >
                  <div
                    className={`question-type list-view`}
                    draggable="true"
                    onDragStart={(e) => handleQuestionTypeDragStart(e, key)}
                    onDragEnd={handleDragEnd}
                    onClick={(e) => handleQuestionTypeClick(key, e)}
                    role="button"
                    tabIndex={0}
                    title={`Add ${value.label} question`}
                    aria-label={`Add ${value.label} question`}
                  >
                    <div className="question-type-content">
                      <span className="question-type-icon" aria-hidden="true">
                        {value.icon}
                      </span>
                      <Typography variant="body2">{value.label}</Typography>
                    </div>
                    <Tooltip
                      title="Add question"
                      placement="right"
                      TransitionComponent={Zoom}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => handleQuestionTypeClick(key, e)}
                        className="add-button"
                        sx={{
                          color: "primary.main",
                          "&:hover": {
                            backgroundColor: "primary.lighter",
                          },
                        }}
                      >
                        <AddCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </ListItem>
              ))}
          </List>
        </div>
      </div>
    );
  };

  const DropIndicator = ({ active, type }) => (
    <div
      className={`drop-indicator ${active ? "active" : ""}`}
      data-type={type ? questionTypes[type].label : ""}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={error ? 6000 : 1000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          width: "100%",
          zIndex: (theme) => theme.zIndex.snackbar + 1
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? "error" : "success"}
          variant="filled"
          elevation={6}
          sx={{ width: "100%", maxWidth: 600 }}
          action={
            error ? (
              <IconButton
                color="inherit"
                size="small"
                onClick={handleCloseSnackbar}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            ) : null
          }
        >
          {error ? (
            <Box>
              {/* <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Survey Not Found
              </Typography> */}
              <Typography variant="body2">
                {error}
              </Typography>
            </Box>
          ) : (
            success
          )}
        </Alert>
      </Snackbar>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton
            edge="start"
            onClick={() => navigate("/dashboard")}
            sx={{ color: "text.primary" }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              ml: 2,
              flex: 1,
              color: "text.primary",
              fontWeight: 600,
              textTransform: "capitalize",
            }}
          >
            {decodeHtmlEntities(surveyData?.name || "Survey Editor")}
          </Typography>

          <TopBarButtons />
        </Toolbar>
      </AppBar>

      <Toolbar />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          overflow: "hidden",
          height: "calc(100vh - 64px)",
          position: "relative",
          mt: 0,
          backgroundColor: "#f7f9fc",
        }}
      >
        {!isMobile && <SidebarQuestionTypes />}

        <div className={`builder-layout ${isMobile ? "mobile" : ""}`}>
          <div
            className="questions-area"
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div className="drop-zone" onDrop={handleDrop}>
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : questions.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#718096",
                    padding: "40px",
                    minHeight: "300px",
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "15px", marginBottom: 2 }}
                  >
                    Drag and drop questions from the sidebar to build your form
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexWrap: "wrap",
                      justifyContent: "center",
                      "& > div": {
                        opacity: 0.6,
                      },
                    }}
                  >
                    {Object.entries(questionTypes).map(([key, value]) => (
                      <div
                        key={key}
                        className="question-type"
                        onClick={() => {
                          const newQuestion = {
                            type: key,
                            title: "",
                            required: false,
                            options: [],
                          };
                          setQuestions((prev) => [...prev, newQuestion]);
                          setTimeout(() => {
                            questionsEndRef.current?.scrollIntoView({
                              behavior: "smooth",
                            });
                          }, 100);
                        }}
                        style={{
                          cursor: "pointer",
                          pointerEvents: "auto",
                        }}
                      >
                        <span className="question-type-icon">{value.icon}</span>
                        {value.label}
                      </div>
                    ))}
                  </Box>
                </Box>
              ) : (
                questions.map((question, index) => (
                  <React.Fragment key={index}>
                    {dropTargetIndex === index &&
                      draggedQuestionIndex !== null && (
                        <DropIndicator
                          active={Boolean(draggedType)}
                          type={draggedType}
                        />
                      )}
                    <div
                      className={`question-wrapper ${selectedQuestion === index ? "selected" : ""
                        } ${draggedQuestionIndex === index ? "dragging" : ""}`}
                      onClick={() => handleQuestionSelect(index)}
                      ref={
                        index === questions.length - 1 ? questionsEndRef : null
                      }
                      data-question-index={index}
                    >
                      <QuestionComponent
                        question={question}
                        onChange={(field, value) =>
                          handleQuestionChange(index, field, value)
                        }
                        onDelete={() => handleQuestionDelete(index)}
                        onAddBelow={() => {
                          setSelectedQuestion(index);
                          setIsQuestionTypesOpen(true);
                        }}
                        index={index}
                        onDragStart={handleQuestionDragStart}
                        onDragEnd={handleQuestionDragEnd}
                        onDragOver={handleQuestionDragOver}
                        onDragLeave={handleQuestionDragLeave}
                        onDrop={handleQuestionDrop}
                        user={user}
                        surveyId={surveyId}
                        setTempImages={setTempImages}
                        hasError={Boolean(questionErrors[index])}
                        errorInfo={questionErrors[index]}
                        isSelected={selectedQuestion === index}
                        onSelect={handleQuestionSelect}
                        allQuestions={questions}
                      />
                    </div>
                    {index === questions.length - 1 &&
                      dropTargetIndex === questions.length && (
                        <DropIndicator
                          active={Boolean(draggedType)}
                          type={draggedType}
                        />
                      )}
                  </React.Fragment>
                ))
              )}
            </div>
          </div>

          {!isMobile &&
            selectedQuestion !== null &&
            questions[selectedQuestion] && (
              <div className="options-panel">
                <QuestionOptions
                  question={questions[selectedQuestion]}
                  onChange={handleQuestionOptionsChange}
                  onClose={() => setSelectedQuestion(null)}
                  allQuestions={questions}
                  surveyId={surveyId || ""}
                  userId={user?.uid || ""}
                />
              </div>
            )}
        </div>

        {/* Add Question Below Selected - appears when a question is selected */}
        {false && selectedQuestion !== null && (
          <Tooltip
            title={`Add question below question ${selectedQuestion + 1}`}
            placement="left"
          >
            <Fab
              color="secondary"
              size="small"
              sx={{
                position: "fixed",
                bottom: isMobile ? 80 : 16,
                right: isMobile ? 16 : 80,
                zIndex: 1049,
              }}
              onClick={() => setIsQuestionTypesOpen(!isQuestionTypesOpen)}
            >
              <AddCircleOutlineIcon />
            </Fab>
          </Tooltip>
        )}

        {isMobile && (
          <Box
            sx={{
              position: "fixed",
              bottom: 16,
              right: 16,
              zIndex: 1050,
            }}
            ref={questionTypesRef}
          >
          
            <Box
              sx={{
                position: "absolute",
                bottom: "100%",
                right: 0,
                minWidth: "180px",
                mb: 2,
                bgcolor: "background.paper",
                borderRadius: 2,
                boxShadow: 3,
                overflow: "hidden",
                transition: "all 0.2s ease-in-out",
                // maxHeight: '60vh', // Limit height to prevent overflow
                visibility: isQuestionTypesOpen ? "visible" : "hidden",
                opacity: isQuestionTypesOpen ? 1 : 0,
                transform: isQuestionTypesOpen
                  ? "translateY(0)"
                  : "translateY(10px)",
                "& .MuiList-root": {
                  maxHeight: "60vh",
                  overflowY: "auto",
                  scrollbarWidth: "thin", // Firefox
                  msOverflowStyle: "auto", // IE/Edge
                  "&::-webkit-scrollbar": {
                    width: "6px", // Chrome/Safari/Opera
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f5f5f5", // Lighter background
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(0, 0, 0, 0.2)", // More subtle color
                    borderRadius: "4px",
                    "&:hover": {
                      background: "rgba(0, 0, 0, 0.3)", // Slightly darker on hover
                    },
                  },
                },
              }}
              className="question-types-menu"
            >
              

              <List sx={{ p: 0 }}>
                {Object.entries(questionTypes).map(([key, value]) => (
                  <ListItem
                    key={key}
                    disablePadding
                    sx={
                      {
                        // borderBottom: '1px solid',
                        // borderColor: 'divider'
                      }
                    }
                  >
                    <Button
                      fullWidth
                      startIcon={value.icon}
                      onClick={() => {
                        handleAddQuestion(key);
                        setIsQuestionTypesOpen(false);
                      }}
                      sx={{
                        justifyContent: "flex-start",
                        px: 2,
                        py: 1.5,
                        color: "text.primary",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      {value.label}
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Box>
            <Tooltip title="Add question at end" placement="left">
              <Fab
                color="primary"
                aria-label="Add Question"
                onClick={() => {
                  // Clear selection to add at end
                  setSelectedQuestion(null);
                  setIsQuestionTypesOpen(!isQuestionTypesOpen);
                }}
                sx={{ display: "none" }}
              >
                <AddIcon />
              </Fab>
            </Tooltip>
          </Box>
        )}
      </Box>
      <SafePreviewSurvey
        open={previewOpen}
        onClose={handlePreviewClose}
        survey={{
          ...surveyData,
          theme: tempTheme || surveyData?.theme,
          layout: surveyLayout,
        }}
        questions={questions}
      />
      <SettingsModal
        open={settingsModalOpen}
        onClose={() => {
          setSettingsModalOpen(false);
        }}
        onThemeSelect={handleThemeSelect}
        onLayoutSelect={(layout) => setSurveyLayout(layout)}
        onSave={() => setSettingsModalOpen(false)}
        selectedTheme={tempTheme || surveyData?.theme}
        surveyId={surveyId}
        user={user}
        surveyData={surveyData}
        onSurveyUpdate={handleSurveyUpdate}
      />
      <PublishModal
        open={publishModalOpen}
        onClose={() => setPublishModalOpen(false)}
        surveyId={surveyId}
        userId={user.uid}
      />

      <LogicRulesModal
        open={logicRulesModalOpen}
        onClose={() => setLogicRulesModalOpen(false)}
        questions={questions}
        surveyId={surveyId}
        userId={user?.uid}
      />

      <ComingSoonDialog
        open={comingSoonDialogOpen}
        onClose={() => setComingSoonDialogOpen(false)}
        feature="Logic Rules"
      />
    </Box>
  );
};

export default SurveyEditor;
